import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type RefObject,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  boardPath,
  type BoardBackgroundCustomApplyBody,
  type BoardBackgroundUploadConfirmBody,
  type BoardBackgroundPut,
  type BoardContentsBoard,
  type BoardFaceList,
  type BoardSnapshotRestoreResponse,
  type BoardRole,
  type BoardTemplate,
  type TemplateCategoryPatch,
  type TemplateVisibilityPatch,
  type BoardVisibilityPatch,
  type CardAgingMode,
  type AutomationAdminReviewCommand,
  type AutomationCommand,
  type AutomationRunOutcome,
  type CreateAutomationCommandBody,
  type DuplicateAutomationCommandBody,
  type DuplicateAutomationCommandResponse,
  type FirstPartyIntegration,
  type FirstPartyIntegrationKey,
  type PowerUpDirectoryEntry,
  type VotingPermission,
} from '@molt/shared';

import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { IconButton } from '@/components/IconButton';
import { SharePanel, type SharePanelItem } from '@/components/share';
import { profileName } from '@/components/Avatar';
import { announce } from '@/lib/announce';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import { cn } from '@/lib/cn';
import { listUndoSourceKey } from '@/lib/undo';
import {
  useMarkBoardTemplate,
  useUnmarkBoardTemplate,
  useUpdateTemplateDescription,
} from '@/lib/board-templates';
import {
  useBoardInviteLink,
  useCreateBoardInviteLink,
  useRevokeBoardInviteLink,
} from '@/lib/invitations';
import { useCopyBoard } from '@/lib/card-copy';
import { boardCapacityQueryOptions } from '@/lib/list-operations';
import {
  useBoardCalendarFeed,
  useEmailToBoardAddress,
  useGenerateBoardCalendarFeedToken,
  useRegenerateEmailToBoardAddress,
  useUpdateBoardCalendarFeed,
  useUpdateEmailToBoardSettings,
} from '@/lib/external-access';
import {
  useBoardSnapshots,
  useRestoreBoardSnapshot,
  useSetBoardSnapshotCaptureAttachments,
  useSetBoardSnapshotRetention,
  useSetBoardSnapshotsEnabled,
} from '@/lib/board-snapshots';
import {
  boardCustomFieldsQueryOptions,
  useCreateCustomFieldDefinition,
  useCreateCustomFieldOption,
  useDeleteCustomFieldDefinition,
  useDeleteCustomFieldOption,
  useMoveCustomFieldDefinition,
  useMoveCustomFieldOption,
  useRenameCustomFieldDefinition,
  useRenameCustomFieldOption,
  useSetCustomFieldHidden,
  useSetCustomFieldShowOnFront,
} from '@/lib/custom-fields';
import {
  useBoardVotingEnabled,
  useBoardVotingHideTallies,
  useBoardVotingPermission,
  useUpdateBoardVotingEnabled,
  useUpdateBoardVotingHideTallies,
  useUpdateBoardVotingPermission,
} from '@/lib/votes';
import { useBoardCardAgingMode, useUpdateBoardCardAgingMode } from '@/lib/card-aging';
import {
  isCardKeyTaken,
  useUpdateBoardCardKey,
  useUpdateBoardCardNumbersSetting,
} from '@/lib/board-card-keys';
import { useUpdateBoardDangerousComments } from '@/lib/board-dangerous-comments';
import { useUpdateBoardRequiredApprovals } from '@/lib/board-required-approvals';
import {
  boardCapabilities,
  ownBoardMember,
  useAddBoardMember,
  useBoardCommenting,
  useBoardMemberPermission,
  useBoardMembers,
  useBoardSelfJoin,
  useCloseBoard,
  useDeleteBoard,
  useRemoveBoardMember,
  useUpdateBoardBackground,
  useApplyCustomBoardBackground,
  useUpdateBoardCommenting,
  useUpdateBoardMemberPermission,
  useUpdateBoardMemberRole,
  useUpdateBoardSelfJoin,
  useUpdateBoardVisibility,
  useCustomBoardBackgrounds,
  useUploadCustomBoardBackground,
  workspaceBoardsQueryOptions,
} from '@/lib/boards';
import {
  loadOlderAutomationCommandLog,
  useBoardAutomations,
  useAdminDisableAutomationCommand,
  useAutomationCommand,
  useAutomationCommandLog,
  useCreateAutomationCommand,
  useDeleteAutomationCommand,
  useDuplicateAutomationCommand,
  useUpdateAutomationCommandDefinition,
  useUpdateAutomationCommandEnabled,
  useWorkspaceAutomationActivity,
  useWorkspaceAutomationAdmin,
  useWorkspaceAutomationUsage,
} from '@/lib/automations';
import { boardLabelsQueryOptions } from '@/lib/labels';
import { useWorkspaceEnterpriseLinkage } from '@/lib/enterprise';
import { isSuperadminSession, useSession } from '@/lib/session';
import { boardSilenceQueryOptions, useSetBoardSilenceState } from '@/lib/notifications';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  ownRole,
  workspaceMembersQueryOptions,
  workspacesQueryOptions,
} from '@/lib/workspaces';
import { ArchivedItemsPanel } from './ArchivedItemsPanel';
import { BoardAutomationSection } from './BoardAutomationSection';
import { BoardActivitySection } from './BoardActivitySection';
import { BoardBackgroundPicker } from './BoardBackgroundPicker';
import { BoardCapacityNotice } from './BoardCapacityNotice';
import { BoardDangerConfirm, type BoardDangerAction } from './BoardDangerConfirm';
import { BoardCalendarFeedSection } from './BoardCalendarFeedSection';
import { BoardEmailToBoardSection } from './BoardEmailToBoardSection';
import { BoardCopyDialog } from './BoardCopyDialog';
import { BoardCustomFieldsSection } from './BoardCustomFieldsSection';
import { BoardInviteLinkSection } from './BoardInviteLinkSection';
import { BoardMemberList } from './BoardMemberList';
import { MobileParityNotice } from './MobileParityNotice';
import { BoardSnapshotSection } from './BoardSnapshotSection';
import { BoardSettingsSection } from './BoardSettingsSection';
import { BoardStickerTray } from './BoardStickerTray';
import {
  VISIBILITY_CONSEQUENCE_KEYS,
  VISIBILITY_LABEL_KEYS,
} from './BoardVisibilityControl';
import { TemplateDescriptionSection } from './TemplateDescriptionSection';
import { TemplateVisibilityControls } from './TemplateVisibilityControls';
import { WatchToggle } from '../watch/WatchToggle';
import {
  PowerUpDirectory,
  type PowerUpDirectoryCategoryFilter,
  type PowerUpDirectoryPricingFilter,
} from '../power-ups/PowerUpDirectory';
import { PowerUpDetailView } from '../power-ups/PowerUpDetailView';
import { BoardPowerUpList } from '../power-ups/BoardPowerUpList';
import { FirstPartyIntegrationPanel } from '../integrations/FirstPartyIntegrationPanel';
import {
  useAuthorizeBoardPowerUp,
  useBoardPowerUps,
  useDisableBoardPowerUp,
  useEnableBoardPowerUp,
  usePowerUpDetail,
  usePowerUpDirectory,
  useRemovePersonalPowerUpSettings,
  useRevokeBoardPowerUpAuthorization,
  useUpdateBoardPowerUpHeaderVisibility,
  useUpdateBoardPowerUpSettings,
  type PowerUpJsonObject,
} from '@/lib/power-ups';
import { useBoardFirstPartyIntegrations } from '@/lib/first-party-integrations';

/**
 * `FE-08.2` — **the board menu**, chunk `D3`, agent B. `FR-BOARD-005`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS IS NOT A MODAL, AND THE REQUIREMENT IS WHAT SAYS SO
 * ---------------------------------------------------------------------------
 * `FR-BOARD-005` AC1, verbatim: "the panel opens beside the board and **the
 * board face remains scrollable**". AC3: Escape closes it "and the board face
 * is unchanged". So: **no focus trap, no scrim, no `inert` on the board face,
 * no `aria-modal`.** An `<aside role="complementary">` with an `aria-label`
 * naming the board.
 *
 * That is why this is a new component and not a `Dialog`. `apps/web` has 45
 * primitives and **none of them is a drawer**: `Dialog` is a centred modal with
 * a trap and a scrim, and `Popover` hangs off its trigger even in its
 * `variant="drawer"` spelling. Both are right for a confirmation — which is why
 * `BoardDangerConfirm` uses `Dialog` — and wrong for this. **Do not widen
 * `Dialog`**; `D2` finding 2 declined exactly that for `Menu.asChild`.
 *
 * ⚠ **`Menu` is also wrong here, and for a reason that would silently reverse a
 * decision this brief took.** `Menu` has no `disabledReason` and its documented
 * rule is that an option the viewer cannot use is **omitted, never disabled**.
 * The close and delete entries are `Button`s in the panel — `Button` carries
 * `disabledReason` — because `D3-BRIEF.md` §2.2 decided, against
 * `FR-BOARD-005`'s "hidden" edge case and in favour of `FR-EXP-012`'s
 * "disabled with a reason", that they are **locked, never hidden**.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE TWO AXES ON THE DESTRUCTIVE ENTRIES, WHICH MUST NOT COLLAPSE
 * ---------------------------------------------------------------------------
 * `D3-BRIEF.md` §2.3. **Offered** is a *state* fact; **enabled** is a
 * *permission* fact, and they are computed from different things:
 *
 * | entry | offered when | enabled when |
 * |---|---|---|
 * | Close board | the board is **open** (`!board.closed`) | `capabilities.canClose` |
 * | Delete board permanently | the board is **closed** (`capabilities.deleteOffered`, `FR-EXP-012` AC4) | `capabilities.canDelete` |
 *
 * ⚠ **`boardCapabilities` has no `closeOffered`**, so the local const below is
 * the whole of that axis for Close. Reaching for `canClose` as the *render*
 * gate is exactly how the two axes collapse into one — and the symptom is a
 * criterion silently lost, not a red test: a non-admin would stop seeing the
 * disabled control that `FR-EXP-012`'s edge case exists to require.
 *
 * Delete is absent at **every** role while the board is open, admin included
 * (AC4, and the server agrees — `DELETE /boards/:id` is a 409 until the board
 * is closed). `deleteDisabledReason` is `null` in that state by design: a
 * reason attached to a control nobody renders would be a phantom one.
 *
 * ⚠ **`rolesKnown` is the members query AND the session**, not the members
 * query alone. `ownBoardRole(members, undefined)` returns `null` — the same
 * value it returns for "you hold no row" — so a `rolesKnown` that ignored a
 * still-pending `profile` would show an admin *"Only a board admin can close
 * this board."* for the width of the session load, which is a wrong sentence
 * rather than a slow one.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT OWNS
 * ---------------------------------------------------------------------------
 * The section stack, the members query, the capability derivation, the close
 * and delete mutations and `BoardDangerConfirm`. It owns the mutations rather
 * than `BoardFace` because that is the house pattern (`CardComposer` calls
 * `useCreateCard`) and because it keeps `BoardFace` — a file three chunks have
 * already edited — to a minimal diff.
 *
 *  - `useBoardMembers(board.id, open)` — ⚠ **`enabled` is `open`**. A board
 *    whose menu is never opened fetches no member list. This is the only reason
 *    D3 calls that route at all: nothing else on the wire carries the caller's
 *    role (`D3-BRIEF.md` §3.3).
 *  - **Escape closes the panel** from anywhere inside it, **including from a
 *    sub-section** — AC3 says *closes*, not *goes back*. When
 *    `BoardDangerConfirm` is open, `Dialog`'s own trap consumes the first
 *    Escape; that is the dismissal ladder working, not a conflict.
 *  - **Back** returns `'archived'` → `'root'` **without closing** (AC2), as a
 *    real `<button>` named *"Back to board menu"*.
 *  - **Destructive entries are visually separated** from routine ones —
 *    `FR-BOARD-005`'s first edge case — by their own group and a separator, not
 *    by colour alone.
 *
 * ---------------------------------------------------------------------------
 * ⚠ REOPEN IS NOT HERE, AND THAT IS `D3-BRIEF.md` §7's DECISION
 * ---------------------------------------------------------------------------
 * The accessible-name table names *"Reopen board"* exactly once, and its home
 * is **`BoardClosedNotice`**, which is already `role="status"` on the board
 * face. So a closed board's panel offers Delete and **no** Close and **no**
 * Reopen: closing an already-closed board is a server no-op, and a second
 * control named "Reopen board" on the same page would make
 * `getByRole('button', { name: 'Reopen board' })` ambiguous in Playwright's
 * strict mode — breaking `FR-EXP-012` AC2's assertion in a way that reads as
 * the board face being wrong. `useReopenBoard` is therefore agent A's export
 * for **`BoardFace`**, not for this file.
 *
 * ⚠ **Accessible names in this panel are chosen so that none is a substring of
 * another.** Playwright's `getByRole(…, { name })` is a case-insensitive
 * *substring* match by default, so a dismiss control named "Close board menu"
 * would be matched by a spec asking for "Close board" and blow strict mode.
 * The dismiss control is **"Hide board menu"**. Keep it that way, and check any
 * name added later against "Close board", "Delete board permanently", "Back to
 * board menu" and "Board menu".
 *
 * ---------------------------------------------------------------------------
 * ⚠ ONE CHANNEL PER EVENT — WHY NO `error` REACHES `BoardDangerConfirm`
 * ---------------------------------------------------------------------------
 * `lib/boards.ts` announces its **own** rollback on every failed mutation, and
 * says in its file docstring that a component composing those hooks must not
 * also render the error through a `Notice`/`role="alert"`
 * (`D3-BRIEF.md` §5.7). `BoardDangerConfirm`'s only error channel *is* a
 * `role="alert"` `Notice`, so this file passes it no `error` at all and the
 * confirmation stays **open** on failure so the action is still retryable.
 * Reported in the chunk return: the consequence is that a sighted mouse user
 * gets no visible feedback on a failed close or delete, and what would satisfy
 * both rules is a non-live visible error node in the confirmation — which its
 * props do not offer.
 *
 * Success: **delete announces from here** (§5.7 — the board face is gone and
 * cannot announce anything), **close does not** (`BoardClosedNotice` appearing
 * is that event's channel).
 *
 * ---------------------------------------------------------------------------
 * `D4` — WHAT THE TWO NEW SUB-SECTIONS ADD, AND THE FOUR TRAPS IN THEM
 * ---------------------------------------------------------------------------
 * `FE-10.1` (**Settings**) and `FE-10.2` (**Members**) are hosted here.
 * `D4-BRIEF.md` §6.7 is the contract: `BoardMenuPanelProps` is **unchanged** —
 * everything the two sections need, this file owns or fetches. They follow
 * `'archived'`'s shipped behaviour rather than re-deciding it: **Back returns
 * to `'root'` without closing the panel**, Escape still closes from anywhere.
 *
 *  1. ⚠ **`memberErrors` is keyed by user id, and a row's entry is cleared
 *     when *that row's* next mutation starts.** `FR-PERM-032`'s last-admin
 *     refusal has to be explained *where it happened* — on the row whose
 *     control was pressed (§5.9). A single shared error string is how that
 *     refusal ends up under the wrong member, and it is the one defect in this
 *     file that no amount of green presence-assertions would find. Both the
 *     remove and the role mutation target a row, so both clear on start.
 *     `addError` is **not** in that record: it is read straight off
 *     `addMutation.error`, which React Query resets when the next `mutate()`
 *     begins.
 *  2. ⚠ **`canManageMembers` and `canChangeRoles` are different questions.**
 *     `POST`/`DELETE /members` sit on `requireBoardMemberManager`, whose ladder
 *     admits a **normal** member while `memberAddRemovePermission` is
 *     `'all_members'` (the default) — so a normal member may add and remove.
 *     `PATCH /members/:id/role` sits on `requireBoardAdminRole`, so the same
 *     member may never change a role. Conflating them would either hide the
 *     add form from most of the product's members or offer a role control that
 *     403s.
 *  3. ⚠ **A third observer of `queryKeys.boards.members(id)` is correct.**
 *     This panel gates on `open`, `BoardFace` on `signedIn`, the board route on
 *     `signedIn && boardId !== undefined` — one key, three `enabled`, one
 *     request (React Query dedupes them). §5.5 forbids "fixing" it into one.
 *     ⚠ **The panel's gate is `open` alone and stays that way**: §3.4's 401
 *     trap (a members query fired while signed out redirects the visitor to
 *     `/sign-in`) cannot fire from here, because `BoardFace` renders the menu
 *     trigger only when `signedIn`. That gate is what makes this one safe —
 *     a chunk that makes the trigger anonymous-reachable must widen this
 *     `enabled` in the same commit.
 *  4. ⚠ **No refusal is announced from this file.** Every hook in
 *     `lib/boards.ts` announces its own rollback with the server's message, so
 *     a failure reaching `announce()` from here as well would be one event on
 *     two channels (§5.7). What this file does with a refusal is put the
 *     server's words **on the row**, which is a *visible* explanation and what
 *     `FR-PERM-032` requires. **Successes are the other way round** — the
 *     hooks never announce them and §7 makes them this file's: added, removed,
 *     role changed, visibility changed.
 *
 * ⚠ **`canChangeRoles` carries a `!closed` conjunct that §6.7 does not
 * spell**, and it is not an embellishment: `PATCH /members/:id/role` throws its
 * own 409 on a closed board for every role, admin included — exactly as
 * `boardCapabilities` already folds `!closed` into `canManageMembers` and
 * `canChangeVisibility`. Without it a closed board would render a role `Menu`
 * beside a suppressed remove control, from two gates that disagree, and every
 * role change would 409.
 *
 * ---------------------------------------------------------------------------
 * ⏭ FORWARD — WHAT LIVES HERE LATER
 * ---------------------------------------------------------------------------
 * The **Archived items** sub-section is a host and nothing more. Browsing,
 * restoring and permanently deleting archived lists and cards are
 * **`FE-22.1`–`22.3` and `FE-11.3`'s panel half, chunk `F3`** — `C2` moved them
 * there by name and on the record, on the basis that this panel would exist.
 * **This file calls no archive route.**
 *
 * The board description, the admins list, background, labels, watch,
 * Power-Ups, automation, print/export and leave board are all entries
 * `FR-BOARD-005`'s *Behaviour* names and none of them has a destination in
 * Phase 1. ⚠ The **description has no wire at all** — no column read, no route,
 * no schema field — so that clause is unmet by absence, not deferred. The
 * activity feed is Phase 2 (`B28`), pinned by `FE-08.2`'s own item line.
 *
 * ---------------------------------------------------------------------------
 * ⚠ HOST LAYOUT CONTRACT (agent H, `BoardFace`)
 * ---------------------------------------------------------------------------
 * The panel carries its own width, its own scroll and its own ink and takes no
 * `className` — the §6.7 signature is fixed. It is `h-full` and `shrink-0`, so
 * it composes two ways and the host picks one:
 *
 *  - as a **row-flex sibling** of the scrolling strip's column — AC1's literal
 *    reading, the board narrows and stays fully visible; or
 *  - inside a **positioned wrapper** (`absolute inset-y-0 right-0 z-20`) over
 *    the right edge of the board face.
 *
 * What it must **not** be mounted as is a sibling *inside* `BoardFace`'s
 * existing column flex, which would put it under the strip rather than beside
 * the board. Nothing here can detect that; the 1440×900 screenshot pass can.
 */

/**
 * ⚠ `'members'` and `'settings'` are `D4`'s (`FE-10.1`, `FE-10.2`). They follow
 * `'archived'`'s shipped behaviour rather than re-deciding it: **Back returns
 * to `'root'` without closing the panel**, and Escape still closes from
 * anywhere inside. `D4-BRIEF.md` §6.7.
 */
export type BoardMenuSection =
  | 'root'
  | 'activity'
  | 'archived'
  | 'members'
  | 'settings'
  | 'background'
  | 'stickers'
  | 'custom-fields'
  | 'integrations'
  | 'power-ups'
  | 'share'
  | 'automation'
  | 'snapshots'
  | 'external-access'
  | 'invite-link';

export interface BoardMenuPanelProps {
  board: BoardContentsBoard;
  lists: readonly BoardFaceList[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mobile?: boolean | undefined;
  /** The trigger, so focus returns to it on close — `FR-EXP-022` AC3. */
  returnFocusRef: RefObject<HTMLElement | null>;
  /** Called after a successful delete; the host navigates away. */
  onDeleted: () => void;
  /** Called after the viewer successfully leaves; the host navigates away. */
  onLeft: () => void;
  template?: BoardTemplate | null | undefined;
  templateLoading?: boolean | undefined;
  onChangeTemplateVisibility?: ((patch: TemplateVisibilityPatch) => void) | undefined;
  onChangeTemplateCategory?: ((patch: TemplateCategoryPatch) => void) | undefined;
  templateVisibilityBusy?: boolean | undefined;
  templateCategoryBusy?: boolean | undefined;
  templateVisibilityError?: string | undefined;
  templateCategoryError?: string | undefined;
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  initialSection?: BoardMenuSection | undefined;
  onOpenShortcuts?: (() => void) | undefined;
  onCollapseAllLists?: (() => void) | undefined;
  onExpandAllLists?: (() => void) | undefined;
  onPermanentListDelete?: ((sourceKey: string) => void) | undefined;
}

type AutomationLogTarget = Pick<
  AutomationCommand,
  'id' | 'name' | 'commandKind' | 'enabled' | 'isBroken' | 'visibility' | 'updatedAt'
>;

/**
 * The in-flight state of `FE-70.2`'s "load older" affordance.
 *
 * ⚠ **The ENTRIES are not here.** `loadOlderAutomationCommandLog` folds an
 * older page onto the cache entry under `queryKeys.automations.log(commandId)`,
 * so the merged log — page one plus everything paged back — is what
 * `useAutomationCommandLog` already returns, and the panel passes that object
 * down by identity. A second accumulation in this file would double-count
 * every older entry against the same cache.
 *
 * What is left is the pair the cache cannot hold: whether a page is in flight,
 * and whether the last one was refused.
 *
 * ⚠ **`commandId` is a field rather than an assumption.** `useState` does not
 * re-key on the command the way a query does. Without it, a spinner or a
 * refusal from command A's log would still be on screen under command B's
 * title, and a response that arrives after the reader has moved on would land
 * on the wrong dialog — with nothing red anywhere. Every read compares it
 * against the live target and every async write refuses to land on a different
 * one, which makes a late response inert rather than wrong.
 *
 * ⚠ **A cursor is only ever a string the server handed back.** The codec is
 * keyset over `(command_id, occurred_at)` and a fabricated cursor is a 422 by
 * design, so nothing in this file constructs one: the value comes from the
 * previous response's `nextCursor` and goes straight back out.
 */
interface AutomationLogOlderState {
  readonly commandId: string;
  readonly loading: boolean;
  /**
   * A refused older page, rendered rather than swallowed. An affordance that
   * silently eats a 422 turns a refusal into an empty page — the same
   * lies-rather-than-omits shape the honoured cursor itself was built to end.
   */
  readonly error: string | null;
}

/**
 * The panel's `<h2>` per section. A total `Record<BoardMenuSection, string>`
 * rather than a chain of ternaries: the day a fifth section is added, the
 * missing heading is a compile error instead of a panel silently titled
 * *"Board menu"* while showing something else.
 *
 * ⚠ None of these is a superstring of another entry's accessible name, and
 * none collides with "Close board" / "Delete board permanently" / "Back to
 * board menu" — the constraint the file docstring states for Playwright's
 * substring matching.
 */
const SECTION_HEADING_KEYS: Record<BoardMenuSection, string> = {
  root: 'boardMenu.title',
  activity: 'boardMenu.activity',
  archived: 'boardMenu.archived',
  members: 'boardMenu.members',
  settings: 'boardMenu.settings',
  background: 'boardMenu.background',
  stickers: 'boardMenu.stickers',
  'custom-fields': 'boardMenu.customFields',
  integrations: 'boardMenu.integrations',
  'power-ups': 'boardMenu.powerUps',
  share: 'boardMenu.share',
  automation: 'boardMenu.automation',
  snapshots: 'boardMenu.snapshots',
  'external-access': 'boardMenu.externalAccess',
  'invite-link': 'boardMenu.inviteLink',
};

function boardMenuMessageKey(section: BoardMenuSection): string {
  return SECTION_HEADING_KEYS[section];
}

function shareVisibility(board: BoardContentsBoard): SharePanelItem['visibility'] {
  if (board.visibility === 'workspace_visible') return 'workspace';
  if (board.visibility === 'enterprise_visible') return 'enterprise';
  return board.visibility;
}

function currentBoardHref(board: BoardContentsBoard): string {
  if (typeof window === 'undefined') return boardPath(board.shortLink, board.slug);
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

const ROLE_ANNOUNCEMENT_KEYS: Record<BoardRole, string> = {
  admin: 'boardMenu.announce.memberRole.admin',
  normal: 'boardMenu.announce.memberRole.normal',
  observer: 'boardMenu.announce.memberRole.observer',
};

function CloseGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden="true" focusable="false">
      <path
        d="M4 4l8 8M12 4l-8 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface LifecycleEntryProps {
  label: string;
  /**
   * ⚠ Non-null **is** the refused state. `boardCapabilities` guarantees that
   * `closeDisabledReason`/`deleteDisabledReason` is non-null exactly when the
   * matching `can*` is false *and the control still renders* (§6.1), so one
   * value carries both facts and they cannot drift apart here.
   *
   * The two branches are written out rather than passing
   * `disabledReason={reason ?? undefined}`: `Button` declares
   * `disabledReason?: string`, and under `exactOptionalPropertyTypes` handing
   * it an explicit `undefined` does not typecheck. A conditional JSX spread
   * would compile — and is the defect-ledger shape this chunk forbids.
   */
  reason: string | null;
  onSelect: () => void;
}

/**
 * One destructive row. `text-bad` is measured against `--color-surface` and
 * `--color-surface-raised` by `styles.tokens.test.ts` (5.58:1 light, 4.54:1
 * dark), so it is legal ink here — but it is **not** what separates these
 * entries from the routine ones. The separator and the labelled group are.
 */
function LifecycleEntry({ label, reason, onSelect }: LifecycleEntryProps): ReactElement {
  const className = 'w-full justify-start text-bad';

  if (reason !== null) {
    return (
      <Button variant="quiet" className={className} disabled disabledReason={reason}>
        {label}
      </Button>
    );
  }

  return (
    <Button variant="quiet" className={className} onClick={onSelect}>
      {label}
    </Button>
  );
}

export function BoardMenuPanel({
  board,
  lists,
  open,
  onOpenChange,
  mobile = false,
  returnFocusRef,
  onDeleted,
  onLeft,
  template = null,
  templateLoading = false,
  onChangeTemplateVisibility,
  onChangeTemplateCategory,
  templateVisibilityBusy,
  templateCategoryBusy,
  templateVisibilityError,
  templateCategoryError,
  onOpenCard,
  initialSection,
  onOpenShortcuts,
  onCollapseAllLists,
  onExpandAllLists,
  onPermanentListDelete,
}: BoardMenuPanelProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  /*
    Held for `loadOlderAutomationCommandLog`, which folds an older log page onto
    the cache entry `useAutomationCommandLog` reads. Every other query in this
    file is declarative; that one is imperative because it is driven by a
    press, not by a render.
  */
  const queryClient = useQueryClient();
  const [section, setSection] = useState<BoardMenuSection>('root');
  const [currentTemplate, setCurrentTemplate] = useState<BoardTemplate | null>(template);
  const [confirming, setConfirming] = useState<BoardDangerAction | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyResult, setCopyResult] = useState<{
    cardsCopied: number;
    path: string;
  } | null>(null);
  const [snapshotRestoreResult, setSnapshotRestoreResult] =
    useState<BoardSnapshotRestoreResponse | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [automationRowErrors, setAutomationRowErrors] = useState<Record<string, string>>(
    {},
  );
  const [automationDuplicateResult, setAutomationDuplicateResult] =
    useState<DuplicateAutomationCommandResponse | null>(null);
  const [automationLogTarget, setAutomationLogTarget] =
    useState<AutomationLogTarget | null>(null);
  const [automationLogOlder, setAutomationLogOlder] =
    useState<AutomationLogOlderState | null>(null);
  const [automationAdminDefinitionTarget, setAutomationAdminDefinitionTarget] =
    useState<AutomationAdminReviewCommand | null>(null);
  const [automationActivityBoardId, setAutomationActivityBoardId] = useState<
    string | null
  >(null);
  const [automationActivityOutcome, setAutomationActivityOutcome] =
    useState<AutomationRunOutcome | null>(null);
  const [automationAdminBoardId, setAutomationAdminBoardId] = useState<string | null>(
    null,
  );
  const [automationAdminRowErrors, setAutomationAdminRowErrors] = useState<
    Record<string, string>
  >({});
  const [integrationRowErrors, setIntegrationRowErrors] = useState<
    Partial<Record<FirstPartyIntegrationKey, string>>
  >({});
  const [integrationDisableTarget, setIntegrationDisableTarget] =
    useState<FirstPartyIntegration | null>(null);
  const [integrationDisableKeepData, setIntegrationDisableKeepData] = useState(false);
  const [powerUpQuery, setPowerUpQuery] = useState('');
  const [powerUpSearch, setPowerUpSearch] = useState('');
  const [powerUpCategory, setPowerUpCategory] =
    useState<PowerUpDirectoryCategoryFilter>('all');
  const [powerUpPricingModel, setPowerUpPricingModel] =
    useState<PowerUpDirectoryPricingFilter>('all');
  const [powerUpMadeByUs, setPowerUpMadeByUs] = useState(false);
  const [powerUpDetailId, setPowerUpDetailId] = useState<string | null>(null);
  const [powerUpRowErrors, setPowerUpRowErrors] = useState<Record<string, string>>({});
  /**
   * ⚠ **Keyed by user id** — §5.9, and trap 1 in the file docstring. One
   * string shared across the list is how the last-admin 409 ends up rendered
   * under a member it did not happen to.
   */
  const [memberErrors, setMemberErrors] = useState<Record<string, string>>({});
  const panelRef = useRef<HTMLElement>(null);
  const shareBoardTriggerRef = useRef<HTMLButtonElement | null>(null);
  /** Was the panel open on the previous render? Guards the focus return. */
  const wasOpen = useRef(false);
  const dangerHeadingId = useId();

  const session = useSession();
  const { profile } = session;
  /**
   * SA-9. Each `|| isSuper` below mirrors a route whose guard admits a caller
   * who holds the board only through the superadmin arm; each gate WITHOUT one
   * mirrors a route that refuses that caller or a standing channel it may not
   * open (`superadmin-site-rulings.md` §8). Never folded into `role`.
   */
  const isSuper = isSuperadminSession(session);
  const sessionReadyOpen = open && profile !== null;
  // ⚠ This is the panel-open gate AND the session gate. A board whose menu is
  // never opened fetches no member list; a menu left open after the profile becomes null
  // stops refetching the five session-only reads named by `FE-61.2`.
  const members = useBoardMembers(board.id, sessionReadyOpen);
  /*
   * `D4`. The same session-ready gate, for the same reason and against a second key:
   * `FR-PERM-018` AC4 ("no add-member control is offered") cannot be answered
   * without `member_add_remove_permission`, and the same response fills the
   * settings surface's second row (§5.6). A board whose menu is never opened
   * fetches neither, and a panel that outlives the session refetches neither.
   */
  const memberPermission = useBoardMemberPermission(board.id, sessionReadyOpen);
  const workspaceMembers = useQuery({
    ...workspaceMembersQueryOptions(board.workspaceId),
    enabled: sessionReadyOpen,
  });
  const commentingPermission = useBoardCommenting(board.id, open);
  const selfJoin = useBoardSelfJoin(board.id, sessionReadyOpen);
  const votingEnabled = useBoardVotingEnabled(board.id, open);
  const votingPermission = useBoardVotingPermission(board.id, open);
  const votingHideTallies = useBoardVotingHideTallies(board.id, open);
  const cardAgingMode = useBoardCardAgingMode(board.id, open);
  const capacity = useQuery({
    ...boardCapacityQueryOptions(board.id),
    enabled: sessionReadyOpen,
  });
  const customFields = useQuery({
    ...boardCustomFieldsQueryOptions(board.id),
    enabled: open && section === 'custom-fields',
  });
  const customBackgrounds = useCustomBoardBackgrounds(
    board.id,
    open && section === 'background',
  );
  const boardSilence = useQuery({
    ...boardSilenceQueryOptions(board.id),
    enabled: open && profile !== null,
  });
  /*
   * -------------------------------------------------------------------------
   * `FE-67.1` / `FR-PERM-011` AC1 — IS THIS BOARD'S WORKSPACE IN AN ENTERPRISE?
   * -------------------------------------------------------------------------
   * The panel is the only component on the path from `BoardFace` to
   * `BoardVisibilityControl` that holds both a `workspaceId` and a query
   * client, so this is where the fact enters the tree. It is threaded to
   * `BoardSettingsSection` and consumed by the control, neither of which
   * fetches anything.
   *
   * ⚠ Same `open && profile !== null` gate as `workspaceMembers` above, and the
   * `open` half is load-bearing against a shipped assertion: "fetches nothing
   * at all for a board whose menu is never opened" compares the request log
   * against `['GET /api/me']` exactly. Gated on `open` rather than on
   * `section === 'settings'` so the answer has settled by the time somebody
   * clicks through to Settings — an Enterprise-linked workspace then paints its
   * fourth row on first render instead of popping it in.
   */
  const workspaceEnterprise = useWorkspaceEnterpriseLinkage(
    board.workspaceId,
    open && profile !== null,
  );

  /**
   * -------------------------------------------------------------------------
   * ⚠ THE POLARITY, AND WHY A 404 IS AN ANSWER RATHER THAN A FAILURE
   * -------------------------------------------------------------------------
   * `FR-PERM-011` AC1: the Enterprise level is offered only on a board whose
   * workspace is linked to an Enterprise. `isSuccess` **and** a non-null id, so
   * the level is suppressed in every state that is not a positive answer:
   *
   *   - pending or disabled — suppressed. Both polarities flicker once for one
   *     population; this one flickers for the rare linked workspace instead of
   *     showing every admin in the product a level they cannot reach, on every
   *     menu open, which is the exact pixel AC1 forbids.
   *   - 200 with `enterpriseId: null` — suppressed. That is the requirement.
   *   - **404 — suppressed, and NOT an error.** The route folds "no such
   *     workspace", "deleted workspace" and "a workspace you are not in" into
   *     one 404 so it cannot be used as an existence oracle, so a board admin
   *     who is not a workspace member reads one legitimately. It is rendered
   *     nowhere: `visibilityError` below stays bound to the MUTATION's error,
   *     which is the only failure a person can act on. Nor is it retried —
   *     `shouldRetryRequest` returns true only for `internal` and
   *     `rate_limited`, so a 4xx stops at the first attempt. A retry loop on
   *     the most-opened panel in the product would be a visible defect.
   *
   * ⚠ Suppression only ever REMOVES an option from a menu. It never changes the
   * held value, the trigger label, the consequence line or the emitted patch,
   * and the control offers the held value unconditionally — a board that is
   * `enterprise_visible` inside a workspace since unlinked keeps its own row.
   * `fk_workspaces_enterprise_id` is `ON DELETE SET NULL`, so that state is one
   * `DELETE` away and is not hypothetical.
   */
  const offerEnterpriseVisibility =
    workspaceEnterprise.isSuccess && workspaceEnterprise.data.enterpriseId !== null;

  const ownMember = ownBoardMember(members.data, profile?.id);
  const role = ownMember?.role ?? null;
  const workspaceRole = ownRole(workspaceMembers.data, profile?.id);
  const canManageSnapshots = profile !== null && (workspaceRole === 'admin' || isSuper);
  // Both halves, per the file docstring: a `null` role means "no row" and
  // "no session yet" alike, and only the second is a *checking* state.
  const rolesKnown = members.isSuccess && profile !== null;

  const capabilities = boardCapabilities({
    role,
    closed: board.closed,
    rolesKnown,
    isSuper,
    t,
    // ⚠ `undefined` until the query settles, and `undefined` is not
    // `'admins'` — `boardCapabilities` folds that in. Passed plainly rather
    // than through a conditional spread: `boardCapabilities`'s parameter is
    // declared `| undefined`, so `exactOptionalPropertyTypes` is satisfied.
    memberPermission: memberPermission.data?.memberAddRemovePermission,
  });
  const inviteLink = useBoardInviteLink(
    board.id,
    open && section === 'invite-link' && capabilities.canManageMembers,
  );
  /**
   * SA-9 — KEEP. `capabilities.canManageMembers` is right for adding a member
   * (the reachable member-manager predicate admits a superadmin), but
   * `POST /boards/:id/invite-link` refuses the caller who holds the board only
   * through the arm: redemption re-checks the creator without it, so the link
   * would be dead on arrival. The server's `isViaSuperadmin`, spelt on the
   * client — no board row, no workspace row — and one prop covers generate and
   * revoke, so both stay locked. While the workspace list is still in flight
   * `workspaceRole` reads `null`, so this fails closed.
   */
  const viaSuperadminOnly = isSuper && role === null && workspaceRole === null;
  const canManageInviteLink = capabilities.canManageMembers && !viaSuperadminOnly;
  const emailToBoard = useEmailToBoardAddress(
    board.id,
    open && section === 'external-access' && !board.closed && role !== 'observer',
  );
  const calendarFeed = useBoardCalendarFeed(
    board.id,
    open && section === 'external-access' && role !== null,
  );
  const snapshots = useBoardSnapshots(
    board.id,
    open && section === 'snapshots' && canManageSnapshots,
  );
  const canManageArchive =
    rolesKnown && !board.closed && ((role !== null && role !== 'observer') || isSuper);
  const archiveDisabledReason = canManageArchive
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.editorArchive')
      : t('boardMenu.reason.checkingBoardAccess');
  /** §2.3's other axis — the one `boardCapabilities` does not carry. */
  const closeOffered = !board.closed;
  /**
   * ⚠ Not `capabilities.canManageMembers` — see trap 2 in the file docstring.
   * This mirrors `requireBoardAdminRole` plus the handler's own closed-board
   * 409, which is the same shape as `canChangeVisibility`; the two are equal
   * today by arithmetic, not by meaning, so they are spelt separately.
   */
  const canChangeRoles = rolesKnown && (role === 'admin' || isSuper) && !board.closed;
  const canChangePermissionSettings = canChangeRoles;
  const permissionSettingsDisabledReason = canChangePermissionSettings
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.adminPermissionSettings')
      : t('boardMenu.reason.checkingBoardAccess');
  const canChangeBackground =
    rolesKnown && !board.closed && ((role !== null && role !== 'observer') || isSuper);
  const backgroundDisabledReason = canChangeBackground
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.editorBackground')
      : t('boardMenu.reason.checkingBoardAccess');
  /**
   * `FE-58.1` — the board-menu sticker tray (`FR-CARD-032` AC1, `FR-EXP-027`
   * AC1). Placing a sticker on a card is a **card edit**, so this is the
   * EDITOR gate `canChangeBackground` and `canManageCustomFields` already
   * spell, and not the admin gate. Spelt out a third time rather than aliased
   * to either of them: the three are equal today by arithmetic and not by
   * meaning, which is the same reason `canChangeRoles` is written separately
   * from `capabilities.canManageMembers` above.
   *
   * ⚠ THE ROOT ENTRY IS NOT DISABLED, and that is deliberate. Reading the
   * board's sticker library is open to anyone who can read the board —
   * `GET /boards/:id/stickers` resolves `currentUser(request)?.id ?? null`
   * rather than `requireUser`, so it answers 200 to an anonymous reader and
   * 200 on a closed board. Only PLACING is gated, so the gate travels *into*
   * the tray as `canPlace`, exactly as `canChange` travels into
   * `BoardBackgroundPicker`. Disabling the entry instead would hide a
   * readable surface behind a permission it does not need.
   */
  const canPlaceStickers =
    rolesKnown && !board.closed && ((role !== null && role !== 'observer') || isSuper);
  const stickersDisabledReason = canPlaceStickers
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.editorStickers')
      : t('boardMenu.reason.checkingBoardAccess');
  const canManageCustomFields =
    rolesKnown && !board.closed && ((role !== null && role !== 'observer') || isSuper);
  const customFieldMobileReason = t('boardMenu.mobile.customFields.bodyShort');
  const customFieldsDisabledReason = canManageCustomFields
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.editorCustomFields')
      : t('boardMenu.reason.checkingBoardAccess');
  // KEEP, SA-9: one prop covers listing AND rule creation, which the server
  // refuses a via-superadmin caller (`refuseRuleAuthoringViaSuperadmin`).
  const canManageAutomation = rolesKnown && !board.closed && role === 'admin';
  const canViewAutomation = profile !== null;
  const canAdminReviewAutomation =
    profile !== null && (workspaceRole === 'admin' || isSuper);
  const automationSectionDisabledReason = canViewAutomation
    ? null
    : t('boardMenu.reason.signInAutomation');
  const automationAdminDisabledReason = canAdminReviewAutomation
    ? null
    : workspaceMembers.isPending && profile !== null
      ? t('boardMenu.reason.checkingWorkspaceRole')
      : t('boardMenu.reason.workspaceAdminAutomationReview');
  const snapshotsDisabledReason = canManageSnapshots
    ? null
    : workspaceMembers.isPending && profile !== null
      ? t('boardMenu.reason.checkingWorkspaceRole')
      : t('boardMenu.reason.workspaceAdminSnapshots');
  const automationDisabledReason = canManageAutomation
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.adminAutomations')
      : t('boardMenu.reason.checkingBoardAccess');
  const canManagePowerUps = rolesKnown && !board.closed && role === 'admin';
  const powerUpMobileReason = t('boardMenu.mobile.powerUps.bodyShort');
  const powerUpsDisabledReason = canManagePowerUps
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.adminPowerUps')
      : t('boardMenu.reason.checkingBoardAccess');
  const mobileBoardHref = currentBoardHref(board);
  const canManageIntegrations = canManagePowerUps;
  const integrationsDisabledReason = powerUpsDisabledReason;
  const automationLabels = useQuery({
    ...boardLabelsQueryOptions(board.id),
    enabled: open && section === 'automation' && canManageAutomation,
  });
  const automationDestinationBoards = useQuery({
    ...workspaceBoardsQueryOptions(board.workspaceId, 'open'),
    enabled: open && section === 'automation' && canViewAutomation,
  });
  const canUseEmailToBoard =
    rolesKnown && !board.closed && role !== null && role !== 'observer';
  const emailToBoardDisabledReason = canUseEmailToBoard
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.editorEmailToBoard')
      : t('boardMenu.reason.checkingBoardAccess');
  const canUseCalendarFeed = rolesKnown && role !== null;
  const calendarFeedDisabledReason = canUseCalendarFeed
    ? null
    : rolesKnown
      ? t('boardMenu.reason.memberCalendarFeed')
      : t('boardMenu.reason.checkingBoardMembership');
  const canEnableCalendarFeed = rolesKnown && role === 'admin' && !board.closed;
  const calendarFeedEnableDisabledReason = canEnableCalendarFeed
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.adminCalendarFeeds')
      : t('boardMenu.reason.checkingBoardMembership');
  const canCopyBoard = rolesKnown && role === 'admin';
  const copyBoardDisabledReason = canCopyBoard
    ? null
    : rolesKnown
      ? t('boardMenu.reason.adminCopyBoard')
      : t('boardMenu.reason.checkingBoardAccess');
  const canLeaveBoard = rolesKnown && role !== null;
  const leaveDisabledReason = canLeaveBoard
    ? null
    : rolesKnown
      ? t('boardMenu.reason.memberLeaveBoard')
      : t('boardMenu.reason.checkingBoardMembership');
  const isTemplate = currentTemplate?.isTemplate === true;
  const canManageTemplate = rolesKnown && (role === 'admin' || isSuper) && !board.closed;
  const templateDisabledReason = canManageTemplate
    ? null
    : rolesKnown
      ? board.closed
        ? t('boardMenu.reason.closedReadonly')
        : t('boardMenu.reason.adminTemplate')
      : t('boardMenu.reason.checkingBoardAccess');
  const makeTemplateDisabledReason =
    ownMember?.isGuest === true
      ? t('boardMenu.reason.guestTemplate')
      : templateDisabledReason;
  const templateActionDisabledReason = templateLoading
    ? t('boardMenu.reason.checkingTemplateSettings')
    : templateDisabledReason;
  const makeTemplateActionDisabledReason = templateLoading
    ? t('boardMenu.reason.checkingTemplateSettings')
    : makeTemplateDisabledReason;

  const closeMutation = useCloseBoard(board.id);
  const deleteMutation = useDeleteBoard(board.id);
  const markTemplateMutation = useMarkBoardTemplate(board.id);
  const unmarkTemplateMutation = useUnmarkBoardTemplate(board.id);
  const descriptionMutation = useUpdateTemplateDescription(board.id);
  const copyBoard = useCopyBoard(t);
  const copyWorkspaces = useQuery({
    ...workspacesQueryOptions(),
    enabled: open && copyOpen,
  });
  const visibilityMutation = useUpdateBoardVisibility(board.id);
  const memberPermissionMutation = useUpdateBoardMemberPermission(board.id);
  const commentingPermissionMutation = useUpdateBoardCommenting(board.id);
  const selfJoinMutation = useUpdateBoardSelfJoin(board.id);
  const votingEnabledMutation = useUpdateBoardVotingEnabled(board.id);
  const votingPermissionMutation = useUpdateBoardVotingPermission(board.id);
  const votingHideTalliesMutation = useUpdateBoardVotingHideTallies(board.id);
  const cardAgingModeMutation = useUpdateBoardCardAgingMode(board.id);
  // docs/notes/card-keys.md — the show-card-numbers toggle and the key editor.
  const cardNumbersMutation = useUpdateBoardCardNumbersSetting(board.id);
  const cardKeyMutation = useUpdateBoardCardKey(board.id);
  const requiredApprovalsMutation = useUpdateBoardRequiredApprovals(board.id);
  const dangerousCommentsMutation = useUpdateBoardDangerousComments(board.id);
  const createInviteLinkMutation = useCreateBoardInviteLink(board.id);
  const revokeInviteLinkMutation = useRevokeBoardInviteLink(board.id);
  const regenerateEmailToBoardMutation = useRegenerateEmailToBoardAddress(board.id);
  const updateEmailToBoardSettingsMutation = useUpdateEmailToBoardSettings(board.id);
  const generateCalendarFeedTokenMutation = useGenerateBoardCalendarFeedToken(board.id);
  const updateCalendarFeedMutation = useUpdateBoardCalendarFeed(board.id);
  const setSnapshotsEnabledMutation = useSetBoardSnapshotsEnabled(board.id);
  const setSnapshotRetentionMutation = useSetBoardSnapshotRetention(board.id);
  const setSnapshotCaptureAttachmentsMutation = useSetBoardSnapshotCaptureAttachments(
    board.id,
  );
  const restoreSnapshotMutation = useRestoreBoardSnapshot(board.id);
  const automations = useBoardAutomations(
    board.id,
    undefined,
    open && section === 'automation' && canManageAutomation,
  );
  const createAutomationMutation = useCreateAutomationCommand(board.id);
  const updateAutomationMutation = useUpdateAutomationCommandDefinition();
  const toggleAutomationMutation = useUpdateAutomationCommandEnabled();
  const deleteAutomationMutation = useDeleteAutomationCommand();
  const duplicateAutomationMutation = useDuplicateAutomationCommand();
  const automationLog = useAutomationCommandLog(
    automationLogTarget?.id ?? '',
    open && section === 'automation' && automationLogTarget !== null,
  );
  const automationAdminDefinition = useAutomationCommand(
    automationAdminDefinitionTarget?.id ?? '',
    open && section === 'automation' && automationAdminDefinitionTarget !== null,
  );
  const automationActivity = useWorkspaceAutomationActivity(
    board.workspaceId,
    {
      boardId: automationActivityBoardId,
      outcome: automationActivityOutcome,
      limit: 25,
    },
    open && section === 'automation' && canViewAutomation,
  );
  const automationUsage = useWorkspaceAutomationUsage(
    board.workspaceId,
    open && section === 'automation' && canViewAutomation,
  );
  const automationAdminReview = useWorkspaceAutomationAdmin(
    board.workspaceId,
    { boardId: automationAdminBoardId },
    open && section === 'automation' && canAdminReviewAutomation,
  );
  const powerUps = usePowerUpDirectory(
    {
      boardId: board.id,
      q: powerUpSearch,
      category: powerUpCategory === 'all' ? null : powerUpCategory,
      pricingModel: powerUpPricingModel === 'all' ? null : powerUpPricingModel,
      madeByUs: powerUpMadeByUs ? true : null,
    },
    open && section === 'power-ups' && powerUpDetailId === null,
  );
  const powerUpDetail = usePowerUpDetail(
    powerUpDetailId ?? '',
    open && section === 'power-ups' && powerUpDetailId !== null,
  );
  const boardPowerUps = useBoardPowerUps(board.id, open && section === 'power-ups');
  const firstPartyIntegrations = useBoardFirstPartyIntegrations(
    board.id,
    open && section === 'integrations',
  );
  const enablePowerUpMutation = useEnableBoardPowerUp(board.id);
  const updatePowerUpHeaderMutation = useUpdateBoardPowerUpHeaderVisibility(board.id);
  const updatePowerUpSettingsMutation = useUpdateBoardPowerUpSettings(board.id);
  const authorizePowerUpMutation = useAuthorizeBoardPowerUp(board.id);
  const revokePowerUpAuthorizationMutation = useRevokeBoardPowerUpAuthorization(board.id);
  const disablePowerUpMutation = useDisableBoardPowerUp(board.id);
  const removePersonalPowerUpSettingsMutation = useRemovePersonalPowerUpSettings(
    board.id,
  );
  const adminDisableAutomationMutation = useAdminDisableAutomationCommand();
  const backgroundMutation = useUpdateBoardBackground(board.id);
  const uploadCustomBackgroundMutation = useUploadCustomBoardBackground(board.id);
  const applyCustomBackgroundMutation = useApplyCustomBoardBackground(board.id);
  const createCustomFieldMutation = useCreateCustomFieldDefinition();
  const renameCustomFieldMutation = useRenameCustomFieldDefinition();
  const hiddenCustomFieldMutation = useSetCustomFieldHidden();
  const showOnFrontCustomFieldMutation = useSetCustomFieldShowOnFront();
  const moveCustomFieldMutation = useMoveCustomFieldDefinition();
  const deleteCustomFieldMutation = useDeleteCustomFieldDefinition();
  const createCustomFieldOptionMutation = useCreateCustomFieldOption();
  const renameCustomFieldOptionMutation = useRenameCustomFieldOption();
  const moveCustomFieldOptionMutation = useMoveCustomFieldOption();
  const deleteCustomFieldOptionMutation = useDeleteCustomFieldOption();
  const addMutation = useAddBoardMember(board.id);
  const removeMutation = useRemoveBoardMember(board.id);
  const roleMutation = useUpdateBoardMemberRole(board.id);
  /** Destructured so the close effect can depend on the function, not the object. */
  const resetAdd = addMutation.reset;
  const boardSilenceMutation = useSetBoardSilenceState();
  const resetBoardSilence = boardSilenceMutation.reset;
  const resetVisibility = visibilityMutation.reset;
  const resetMemberPermission = memberPermissionMutation.reset;
  const resetCommentingPermission = commentingPermissionMutation.reset;
  const resetSelfJoin = selfJoinMutation.reset;
  const resetVotingEnabled = votingEnabledMutation.reset;
  const resetVotingPermission = votingPermissionMutation.reset;
  const resetVotingHideTallies = votingHideTalliesMutation.reset;
  const resetCardAgingMode = cardAgingModeMutation.reset;
  const resetCardNumbers = cardNumbersMutation.reset;
  const resetCardKey = cardKeyMutation.reset;
  const resetCreateInviteLink = createInviteLinkMutation.reset;
  const resetRevokeInviteLink = revokeInviteLinkMutation.reset;
  const resetRegenerateEmailToBoard = regenerateEmailToBoardMutation.reset;
  const resetUpdateEmailToBoardSettings = updateEmailToBoardSettingsMutation.reset;
  const resetGenerateCalendarFeedToken = generateCalendarFeedTokenMutation.reset;
  const resetUpdateCalendarFeed = updateCalendarFeedMutation.reset;
  const resetSetSnapshotsEnabled = setSnapshotsEnabledMutation.reset;
  const resetSetSnapshotRetention = setSnapshotRetentionMutation.reset;
  const resetSetSnapshotCaptureAttachments = setSnapshotCaptureAttachmentsMutation.reset;
  const resetRestoreSnapshot = restoreSnapshotMutation.reset;
  const resetCreateAutomation = createAutomationMutation.reset;
  const resetUpdateAutomation = updateAutomationMutation.reset;
  const resetToggleAutomation = toggleAutomationMutation.reset;
  const resetDeleteAutomation = deleteAutomationMutation.reset;
  const resetDuplicateAutomation = duplicateAutomationMutation.reset;
  const resetAdminDisableAutomation = adminDisableAutomationMutation.reset;
  const resetEnablePowerUp = enablePowerUpMutation.reset;
  const resetUpdatePowerUpHeader = updatePowerUpHeaderMutation.reset;
  const resetUpdatePowerUpSettings = updatePowerUpSettingsMutation.reset;
  const resetAuthorizePowerUp = authorizePowerUpMutation.reset;
  const resetRevokePowerUpAuthorization = revokePowerUpAuthorizationMutation.reset;
  const resetCreateCustomField = createCustomFieldMutation.reset;
  const resetRenameCustomField = renameCustomFieldMutation.reset;
  const resetHiddenCustomField = hiddenCustomFieldMutation.reset;
  const resetShowOnFrontCustomField = showOnFrontCustomFieldMutation.reset;
  const resetMoveCustomField = moveCustomFieldMutation.reset;
  const resetDeleteCustomField = deleteCustomFieldMutation.reset;
  const resetCreateCustomFieldOption = createCustomFieldOptionMutation.reset;
  const resetRenameCustomFieldOption = renameCustomFieldOptionMutation.reset;
  const resetMoveCustomFieldOption = moveCustomFieldOptionMutation.reset;
  const resetDeleteCustomFieldOption = deleteCustomFieldOptionMutation.reset;
  const resetCopyBoard = copyBoard.reset;
  const resetTemplateMark = markTemplateMutation.reset;
  const resetTemplateUnmark = unmarkTemplateMutation.reset;
  const resetTemplateDescription = descriptionMutation.reset;

  useEffect(() => {
    setCurrentTemplate(template);
  }, [template]);

  useEffect(() => {
    if (isTemplate && section === 'activity') setSection('root');
  }, [isTemplate, section]);

  /**
   * Which row is mid-flight, read off the mutation's own `variables` rather
   * than mirrored into state — one source of truth, and nothing to leak if a
   * mutation settles while the section is being switched. Remove and role are
   * separate mutations but share one slot: no row can be in both at once,
   * because the control that started either is `busy` for the duration.
   */
  const busyMemberId = removeMutation.isPending
    ? (removeMutation.variables?.userId ?? null)
    : roleMutation.isPending
      ? (roleMutation.variables?.userId ?? null)
      : null;

  /** The row's own error entry, cleared as that row's next mutation starts. */
  function clearMemberError(userId: string): void {
    setMemberErrors((previous) => {
      if (previous[userId] === undefined) return previous;
      const next = { ...previous };
      delete next[userId];
      return next;
    });
  }

  function recordMemberError(userId: string, message: string): void {
    setMemberErrors((previous) => ({ ...previous, [userId]: message }));
  }

  /**
   * The name to announce, resolved from the cached list **before** the
   * mutation runs — after a successful removal the row is gone, so reading it
   * afterwards would announce the fallback.
   */
  function memberName(userId: string): string {
    const found = members.data?.find((candidate) => candidate.id === userId);
    return found === undefined ? t('boardMenu.memberFallback') : profileName(found);
  }

  function handleChangeVisibility(patch: BoardVisibilityPatch): void {
    visibilityMutation.mutate(patch, {
      onSuccess: (updated) => {
        // §7: the picker's label changing announces nothing by itself. The
        // consequence rides along because it is the half of the change that
        // is not visible from the label — `FE-09.1`'s "each level's
        // consequence stated in the UI".
        announce(
          t('boardMenu.announce.visibilityChanged', {
            visibility: t(VISIBILITY_LABEL_KEYS[updated.visibility]),
            consequence: t(VISIBILITY_CONSEQUENCE_KEYS[updated.visibility]),
          }),
        );
      },
    });
  }

  function handleChangeMemberPermission(
    memberAddRemovePermission: 'admins' | 'all_members',
  ): void {
    memberPermissionMutation.mutate(
      { memberAddRemovePermission },
      {
        onSuccess: (updated) => {
          announce(
            updated.memberAddRemovePermission === 'admins'
              ? t('boardMenu.announce.memberManagement.admins')
              : t('boardMenu.announce.memberManagement.allMembers'),
          );
        },
      },
    );
  }

  function handleChangeCommentingPermission(
    commentingPermissionValue:
      'disabled' | 'members' | 'members_and_observers' | 'workspace_members' | 'any_user',
  ): void {
    commentingPermissionMutation.mutate(
      { commentingPermission: commentingPermissionValue },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.commentingChanged'));
        },
      },
    );
  }

  function handleChangeSelfJoin(workspaceSelfJoinEnabled: boolean): void {
    selfJoinMutation.mutate(
      { workspaceSelfJoinEnabled },
      {
        onSuccess: (updated) => {
          announce(
            updated.workspaceSelfJoinEnabled
              ? t('boardMenu.announce.selfJoin.enabled')
              : t('boardMenu.announce.selfJoin.disabled'),
          );
        },
      },
    );
  }

  function handleChangeVotingEnabled(nextVotingEnabled: boolean): void {
    votingEnabledMutation.mutate(
      { votingEnabled: nextVotingEnabled },
      {
        onSuccess: (updated) => {
          announce(
            updated.votingEnabled
              ? t('boardMenu.announce.voting.enabled')
              : t('boardMenu.announce.voting.disabled'),
          );
        },
      },
    );
  }

  function handleChangeVotingPermission(nextVotingPermission: VotingPermission): void {
    votingPermissionMutation.mutate(
      { votingPermission: nextVotingPermission },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.votingPermissionChanged'));
        },
      },
    );
  }

  function handleChangeVotingHideTallies(nextVotingHideTallies: boolean): void {
    votingHideTalliesMutation.mutate(
      { votingHideTallies: nextVotingHideTallies },
      {
        onSuccess: (updated) => {
          announce(
            updated.votingHideTallies
              ? t('boardMenu.announce.votingTallies.hidden')
              : t('boardMenu.announce.votingTallies.visible'),
          );
        },
      },
    );
  }

  function handleChangeCardAgingMode(nextCardAgingMode: CardAgingMode): void {
    cardAgingModeMutation.mutate(
      { cardAgingMode: nextCardAgingMode },
      {
        onSuccess: (updated) => {
          switch (updated.cardAgingMode) {
            case 'off':
              announce(t('boardMenu.announce.cardAging.off'));
              break;
            case 'regular':
              announce(t('boardMenu.announce.cardAging.regular'));
              break;
            case 'pirate':
              announce(t('boardMenu.announce.cardAging.pirate'));
              break;
          }
        },
      },
    );
  }

  function handleChangeShowCardNumbers(shown: boolean): void {
    cardNumbersMutation.mutate(
      { showCardNumbersOnFront: shown },
      {
        onSuccess: (updated) => {
          announce(
            updated.showCardNumbersOnFront
              ? t('boardMenu.announce.cardNumbers.shown')
              : t('boardMenu.announce.cardNumbers.hidden'),
          );
        },
      },
    );
  }

  function handleSaveRequiredApprovals(requiredApprovals: number): void {
    requiredApprovalsMutation.mutate(
      { requiredApprovals },
      {
        onSuccess: (updated) => {
          announce(
            t('boardMenu.announce.requiredApprovals.changed', {
              count: updated.requiredApprovals,
            }),
          );
        },
      },
    );
  }

  function handleSaveDangerousComments(dangerousComments: number | null): void {
    dangerousCommentsMutation.mutate(
      { dangerousComments },
      {
        onSuccess: (updated) => {
          announce(
            updated.dangerousComments === null
              ? t('boardMenu.announce.dangerousComments.cleared')
              : t('boardMenu.announce.dangerousComments.changed', {
                  count: updated.dangerousComments,
                }),
          );
        },
      },
    );
  }

  function handleSaveCardKey(key: string): void {
    cardKeyMutation.mutate(
      { key },
      {
        onSuccess: (updated) => {
          announce(t('boardMenu.announce.cardKey.changed', { key: updated.cardKey }));
        },
      },
    );
  }

  function handleChangeBoardSilence(nextSilenced: boolean): void {
    boardSilenceMutation.mutate(
      { boardId: board.id, silenced: nextSilenced },
      {
        onSuccess: (state) => {
          announce(
            state.silenced
              ? t('boardMenu.silence.enabled')
              : t('boardMenu.silence.disabled'),
          );
        },
      },
    );
  }

  function handleCreateInviteLink(role?: 'normal' | 'observer'): void {
    createInviteLinkMutation.mutate(role === undefined ? undefined : { role }, {
      onSuccess: () => {
        announce(
          role === 'observer'
            ? t('boardMenu.announce.invite.observerGenerated')
            : t('boardMenu.announce.invite.generated'),
        );
      },
    });
  }

  function handleRevokeInviteLink(): void {
    revokeInviteLinkMutation.mutate(undefined, {
      onSuccess: () => {
        announce(t('boardMenu.announce.invite.revoked'));
      },
    });
  }

  function handleRegenerateEmailToBoard(): void {
    regenerateEmailToBoardMutation.mutate(undefined, {
      onSuccess: () => {
        announce(t('boardMenu.announce.emailToBoard.reset'));
      },
    });
  }

  function handleSaveEmailToBoardSettings(input: {
    listId: string;
    position: 'top' | 'bottom';
  }): void {
    updateEmailToBoardSettingsMutation.mutate(input, {
      onSuccess: () => {
        announce(t('boardMenu.announce.emailToBoard.saved'));
      },
    });
  }

  function handleGenerateCalendarFeedToken(): void {
    generateCalendarFeedTokenMutation.mutate(undefined, {
      onSuccess: () => {
        announce(t('boardMenu.announce.calendarFeed.reset'));
      },
    });
  }

  function handleSetCalendarFeedEnabled(enabled: boolean): void {
    updateCalendarFeedMutation.mutate(
      { enabled },
      {
        onSuccess: (updated) => {
          announce(
            updated.enabled
              ? t('boardMenu.announce.calendarFeed.enabled')
              : t('boardMenu.announce.calendarFeed.disabled'),
          );
        },
      },
    );
  }

  function handleSetSnapshotsEnabled(enabled: boolean): void {
    setSnapshotsEnabledMutation.mutate(enabled, {
      onSuccess: (updated) => {
        announce(
          updated.enabled
            ? t('boardMenu.announce.snapshots.enabled')
            : t('boardMenu.announce.snapshots.disabled'),
        );
      },
    });
  }

  function handleSetSnapshotRetention(retentionDays: number | null): void {
    setSnapshotRetentionMutation.mutate(retentionDays, {
      onSuccess: (updated) => {
        announce(
          t(
            'boardMenu.announce.snapshots.retentionChanged',
            { count: updated.retentionDays },
            updated.retentionDays,
          ),
        );
      },
    });
  }

  function handleSetSnapshotCaptureAttachments(captureAttachments: boolean): void {
    setSnapshotCaptureAttachmentsMutation.mutate(captureAttachments, {
      onSuccess: (updated) => {
        announce(
          updated.captureAttachments
            ? t('boardMenu.announce.snapshots.attachmentsIncluded')
            : t('boardMenu.announce.snapshots.attachmentsSkipped'),
        );
      },
    });
  }

  function handleRestoreSnapshot(snapshotId: string, title?: string): void {
    setSnapshotRestoreResult(null);
    restoreSnapshotMutation.mutate(
      title === undefined ? { snapshotId } : { snapshotId, title },
      {
        onSuccess: (result) => {
          setSnapshotRestoreResult(result);
          announce(
            t('boardMenu.announce.snapshots.restored', {
              board: result.board.title,
            }),
          );
        },
      },
    );
  }

  function clearAutomationError(commandId: string): void {
    setAutomationRowErrors((previous) => {
      if (previous[commandId] === undefined) return previous;
      const next = { ...previous };
      delete next[commandId];
      return next;
    });
  }

  function clearAutomationAdminError(commandId: string): void {
    setAutomationAdminRowErrors((previous) => {
      if (previous[commandId] === undefined) return previous;
      const next = { ...previous };
      delete next[commandId];
      return next;
    });
  }

  function handleCreateAutomation(body: CreateAutomationCommandBody): void {
    createAutomationMutation.mutate(body, {
      onSuccess: (command) => {
        announce(t('boardMenu.announce.automation.created', { name: command.name }));
      },
    });
  }

  function handleUpdateAutomation(
    commandId: string,
    body: CreateAutomationCommandBody,
  ): void {
    clearAutomationError(commandId);
    updateAutomationMutation.mutate(
      { boardId: board.id, commandId, body },
      {
        onSuccess: (command) => {
          announce(t('boardMenu.announce.automation.updated', { name: command.name }));
        },
        onError: (error) => {
          setAutomationRowErrors((previous) => ({
            ...previous,
            [commandId]: error.message,
          }));
        },
      },
    );
  }

  function handleToggleAutomation(commandId: string, enabled: boolean): void {
    clearAutomationError(commandId);
    toggleAutomationMutation.mutate(
      { boardId: board.id, commandId, enabled },
      {
        onSuccess: (lifecycle) => {
          announce(
            lifecycle.enabled
              ? t('boardMenu.announce.automation.enabled', { name: lifecycle.name })
              : t('boardMenu.announce.automation.disabled', { name: lifecycle.name }),
          );
        },
        onError: (error) => {
          setAutomationRowErrors((previous) => ({
            ...previous,
            [commandId]: error.message,
          }));
        },
      },
    );
  }

  function handleDeleteAutomation(commandId: string): void {
    clearAutomationError(commandId);
    /* ⚠ The fallback REUSES `boardMenu.automation` — the menu row that opens
       this very section — rather than declaring a second author of the bytes
       `Automation`. `board-dashboard.routing.test.ts` pins that value to
       exactly five keys and is not this slice's to edit. */
    const name =
      automations.data?.commands.find((command) => command.id === commandId)?.name ??
      t('boardMenu.automation');
    deleteAutomationMutation.mutate(
      { boardId: board.id, commandId },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.automation.deleted', { name }));
        },
        onError: (error) => {
          setAutomationRowErrors((previous) => ({
            ...previous,
            [commandId]: error.message,
          }));
        },
      },
    );
  }

  function handleDuplicateAutomation(
    commandId: string,
    body: DuplicateAutomationCommandBody,
  ): void {
    clearAutomationError(commandId);
    setAutomationDuplicateResult(null);
    duplicateAutomationMutation.mutate(
      { commandId, body },
      {
        onSuccess: (result) => {
          setAutomationDuplicateResult(result);
          announce(
            t('boardMenu.announce.automation.copied', { name: result.command.name }),
          );
        },
        onError: (error) => {
          setAutomationRowErrors((previous) => ({
            ...previous,
            [commandId]: error.message,
          }));
        },
      },
    );
  }

  function handleAdminDisableAutomation(
    commandId: string,
    commandBoardId: string,
    reason?: string,
  ): void {
    clearAutomationAdminError(commandId);
    adminDisableAutomationMutation.mutate(
      {
        commandId,
        workspaceId: board.workspaceId,
        boardId: commandBoardId,
        ...(reason === undefined ? {} : { reason }),
      },
      {
        onSuccess: (result) => {
          announce(
            t('boardMenu.announce.automation.adminDisabled', {
              name: result.command.name,
            }),
          );
        },
        onError: (error) => {
          setAutomationAdminRowErrors((previous) => ({
            ...previous,
            [commandId]: error.message,
          }));
        },
      },
    );
  }

  function enabledPowerUpName(boardPowerUpId: string): string {
    return (
      boardPowerUps.data?.powerUps.find((entry) => entry.id === boardPowerUpId)?.powerUp
        .name ?? t('boardMenu.powerUpFallback')
    );
  }

  /**
   * The "load older" state that belongs to the log currently open, or `null`.
   *
   * The id comparison is what makes a stale spinner or a stale refusal
   * unreachable: state left over from a command whose log has since been closed
   * can never be read back out, whether or not the reset below fired.
   */
  const automationLogOlderForTarget =
    automationLogOlder !== null &&
    automationLogTarget !== null &&
    automationLogOlder.commandId === automationLogTarget.id
      ? automationLogOlder
      : null;

  function handleOpenAutomationLog(command: AutomationLogTarget): void {
    setAutomationLogOlder(null);
    setAutomationLogTarget(command);
  }

  function handleCloseAutomationLog(): void {
    setAutomationLogOlder(null);
    setAutomationLogTarget(null);
  }

  /**
   * `FR-AUTO-013` — walk back through the log a page at a time.
   *
   * ⚠ **The cursor is read from the response and never built here.**
   * `automationLog.data` is the merged log the cache holds, so its `nextCursor`
   * is the *tail* cursor — the page after the last one loaded — not page one's.
   * That is what stops the affordance from fetching the same second page for
   * ever, and it is why nothing in this file tracks a cursor of its own.
   *
   * ⚠ **A refusal is recorded, not swallowed.**
   * `loadOlderAutomationCommandLog` deliberately does not catch, so the 422 a
   * fabricated cursor earns — and any transport failure — arrives here and is
   * rendered beside the entries already on screen. The entries are untouched by
   * a failure: the fold happens only after the request resolves.
   */
  function handleLoadOlderAutomationLog(): void {
    const commandId = automationLogTarget?.id;
    if (commandId === undefined) return;
    if (automationLogOlderForTarget?.loading === true) return;
    const cursor = automationLog.data?.nextCursor ?? null;
    if (cursor === null) return;

    setAutomationLogOlder({ commandId, loading: true, error: null });

    void loadOlderAutomationCommandLog(queryClient, commandId, cursor)
      .then(() => {
        setAutomationLogOlder((current) =>
          current?.commandId !== commandId
            ? current
            : { commandId, loading: false, error: null },
        );
      })
      .catch((failure: unknown) => {
        setAutomationLogOlder((current) =>
          current?.commandId !== commandId
            ? current
            : {
                commandId,
                loading: false,
                error: failure instanceof Error ? failure.message : String(failure),
              },
        );
      });
  }

  function clearPowerUpError(boardPowerUpId: string): void {
    setPowerUpRowErrors((previous) => {
      if (previous[boardPowerUpId] === undefined) return previous;
      const next = { ...previous };
      delete next[boardPowerUpId];
      return next;
    });
  }

  function recordPowerUpError(boardPowerUpId: string, message: string): void {
    setPowerUpRowErrors((previous) => ({
      ...previous,
      [boardPowerUpId]: message,
    }));
  }

  function handleEnablePowerUp(powerUp: PowerUpDirectoryEntry): void {
    enablePowerUpMutation.mutate(
      { powerUpId: powerUp.id },
      {
        onSuccess: (enabled) => {
          announce(
            t('boardMenu.announce.powerUps.enabled', {
              powerUp: enabled.powerUp.name,
            }),
          );
        },
        onError: (error) => {
          setPowerUpRowErrors((previous) => ({
            ...previous,
            [powerUp.id]: error.message,
          }));
        },
      },
    );
  }

  function firstPartyIntegration(key: FirstPartyIntegrationKey) {
    return firstPartyIntegrations.data?.integrations.find(
      (integration) => integration.key === key,
    );
  }

  function clearIntegrationError(key: FirstPartyIntegrationKey): void {
    setIntegrationRowErrors((previous) => {
      if (previous[key] === undefined) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }

  function recordIntegrationError(key: FirstPartyIntegrationKey, message: string): void {
    setIntegrationRowErrors((previous) => ({ ...previous, [key]: message }));
  }

  function handleUnavailableIntegrationAction(
    key: FirstPartyIntegrationKey,
    action: string,
  ): void {
    const integration = firstPartyIntegration(key);
    recordIntegrationError(
      key,
      t('boardMenu.integration.unavailableAction', {
        action,
        integration: integration?.name ?? t('boardMenu.integration.thisIntegration'),
      }),
    );
  }

  function handleEnableFirstPartyIntegration(key: FirstPartyIntegrationKey): void {
    clearIntegrationError(key);
    const integration = firstPartyIntegration(key);
    if (integration?.powerUpId === null || integration?.powerUpId === undefined) {
      recordIntegrationError(
        key,
        t('boardMenu.integration.noPowerUpRow', {
          integration: integration?.name ?? t('boardMenu.integration.genericIntegration'),
        }),
      );
      return;
    }
    enablePowerUpMutation.mutate(
      { powerUpId: integration.powerUpId },
      {
        onSuccess: (enabled) => {
          announce(
            t('boardMenu.integration.enabled', {
              integration: enabled.powerUp.name,
            }),
          );
          void firstPartyIntegrations.refetch();
        },
        onError: (error) => {
          recordIntegrationError(key, error.message);
        },
      },
    );
  }

  function handleDisableFirstPartyIntegration(key: FirstPartyIntegrationKey): void {
    clearIntegrationError(key);
    const integration = firstPartyIntegration(key);
    if (
      integration?.boardPowerUpId === null ||
      integration?.boardPowerUpId === undefined
    ) {
      recordIntegrationError(
        key,
        t('boardMenu.integration.notEnabled', {
          integration: integration?.name ?? t('boardMenu.integration.genericIntegration'),
        }),
      );
      return;
    }
    setIntegrationDisableKeepData(false);
    setIntegrationDisableTarget(integration);
  }

  function confirmDisableFirstPartyIntegration(): void {
    const integration = integrationDisableTarget;
    if (integration?.boardPowerUpId == null) {
      return;
    }
    disablePowerUpMutation.mutate(
      {
        boardPowerUpId: integration.boardPowerUpId,
        keepData: integrationDisableKeepData,
      },
      {
        onSuccess: () => {
          announce(
            t('boardMenu.integration.disabled', {
              integration: integration.name,
            }),
          );
          setIntegrationDisableTarget(null);
          void firstPartyIntegrations.refetch();
        },
        onError: (error) => {
          recordIntegrationError(integration.key, error.message);
        },
      },
    );
  }

  function handleTogglePowerUpHeader(boardPowerUpId: string, visible: boolean): void {
    clearPowerUpError(boardPowerUpId);
    const name = enabledPowerUpName(boardPowerUpId);
    updatePowerUpHeaderMutation.mutate(
      { boardPowerUpId, visible },
      {
        onSuccess: () => {
          announce(
            visible
              ? t('boardMenu.announce.powerUps.headerShown', { powerUp: name })
              : t('boardMenu.announce.powerUps.headerHidden', { powerUp: name }),
          );
        },
        onError: (error) => {
          recordPowerUpError(boardPowerUpId, error.message);
        },
      },
    );
  }

  function handleSavePowerUpSettings(
    boardPowerUpId: string,
    settings: PowerUpJsonObject,
  ): void {
    clearPowerUpError(boardPowerUpId);
    const name = enabledPowerUpName(boardPowerUpId);
    updatePowerUpSettingsMutation.mutate(
      { boardPowerUpId, settings },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.powerUps.settingsSaved', { powerUp: name }));
        },
        onError: (error) => {
          recordPowerUpError(boardPowerUpId, error.message);
        },
      },
    );
  }

  function handleAuthorizePowerUp(boardPowerUpId: string): void {
    clearPowerUpError(boardPowerUpId);
    const name = enabledPowerUpName(boardPowerUpId);
    authorizePowerUpMutation.mutate(
      { boardPowerUpId },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.powerUps.authorised', { powerUp: name }));
        },
        onError: (error) => {
          recordPowerUpError(boardPowerUpId, error.message);
        },
      },
    );
  }

  function handleRevokePowerUpAuthorization(boardPowerUpId: string): void {
    clearPowerUpError(boardPowerUpId);
    const name = enabledPowerUpName(boardPowerUpId);
    revokePowerUpAuthorizationMutation.mutate(
      { boardPowerUpId },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.powerUps.disconnected', { powerUp: name }));
        },
        onError: (error) => {
          recordPowerUpError(boardPowerUpId, error.message);
        },
      },
    );
  }

  function handleDisablePowerUp(boardPowerUpId: string, keepData: boolean): void {
    clearPowerUpError(boardPowerUpId);
    const name = enabledPowerUpName(boardPowerUpId);
    disablePowerUpMutation.mutate(
      { boardPowerUpId, keepData },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.powerUps.disabled', { powerUp: name }));
        },
        onError: (error) => {
          recordPowerUpError(boardPowerUpId, error.message);
        },
      },
    );
  }

  function handleRemovePersonalPowerUpSettings(boardPowerUpId: string): void {
    clearPowerUpError(boardPowerUpId);
    const name = enabledPowerUpName(boardPowerUpId);
    removePersonalPowerUpSettingsMutation.mutate(
      { boardPowerUpId },
      {
        onSuccess: () => {
          announce(
            t('boardMenu.announce.powerUps.personalSettingsRemoved', {
              powerUp: name,
            }),
          );
        },
        onError: (error) => {
          recordPowerUpError(boardPowerUpId, error.message);
        },
      },
    );
  }

  function handleChangeBackground(background: BoardBackgroundPut): void {
    backgroundMutation.mutate(background, {
      onSuccess: () => {
        announce(t('boardMenu.backgroundChanged'));
      },
    });
  }

  function handleUploadCustomBackground(input: {
    readonly file: File;
    readonly renderMode: BoardBackgroundUploadConfirmBody['renderMode'];
    readonly textTreatment: BoardBackgroundUploadConfirmBody['textTreatment'];
    readonly applyToBoard: boolean;
  }): void {
    uploadCustomBackgroundMutation.mutate(input, {
      onSuccess: () => {
        announce(t('boardMenu.customBackgroundUploaded'));
      },
    });
  }

  function handleApplyCustomBackground(
    backgroundId: string,
    body: BoardBackgroundCustomApplyBody,
  ): void {
    applyCustomBackgroundMutation.mutate(
      { backgroundId, ...body },
      {
        onSuccess: () => {
          announce(t('boardMenu.backgroundChanged'));
        },
      },
    );
  }

  function rememberTemplate(updated: BoardTemplate): void {
    setCurrentTemplate(updated);
  }

  function handleMakeTemplate(): void {
    markTemplateMutation.mutate(undefined, {
      onSuccess: (updated) => {
        rememberTemplate(updated);
        announce(t('boardMenu.announce.templates.marked', { board: board.title }));
      },
    });
  }

  function handleRemoveTemplateFlag(): void {
    unmarkTemplateMutation.mutate(undefined, {
      onSuccess: (updated) => {
        rememberTemplate(updated);
        announce(t('boardMenu.announce.templates.unmarked', { board: board.title }));
      },
    });
  }

  function handleSaveTemplateDescription(description: string | null): void {
    descriptionMutation.mutate(
      { description },
      {
        onSuccess: (updated) => {
          rememberTemplate(updated);
          announce(t('boardMenu.announce.templates.descriptionSaved'));
        },
      },
    );
  }

  function handleCopyBoard(input: {
    workspaceId: string;
    visibility: 'private' | 'workspace_visible' | 'public';
    keepCards: boolean;
    title: string;
    titleEdited: boolean;
  }): void {
    copyBoard.mutate(
      {
        sourceBoardId: board.id,
        sourceTitle: board.title,
        workspaceId: input.workspaceId,
        visibility: input.visibility,
        keepCards: input.keepCards,
        title: input.titleEdited ? input.title : undefined,
      },
      {
        onSuccess: (result) => {
          announce(t('boardMenu.announce.boardCopied', { title: result.board.title }));
          setCopyResult({
            cardsCopied: result.cardsCopied,
            path: boardPath(result.board.shortLink, result.board.slug),
          });
        },
      },
    );
  }

  function closeCopyDialog(): void {
    setCopyOpen(false);
    setCopyResult(null);
    resetCopyBoard();
  }

  function handleAddMember(handle: { email: string } | { username: string }): void {
    addMutation.mutate(handle, {
      onSuccess: (result) => {
        const name = profileName(result.member);
        // ⚠ `addBoardMemberResultSchema.added` is `false` for somebody who was
        // already on the board — the route is idempotent. Announcing "was
        // added" there would be a sentence with no visual corroboration
        // (the list is unchanged) saying something that did not happen.
        announce(
          result.added
            ? t('boardMenu.announce.memberAdded', { member: name })
            : t('boardMenu.announce.memberAlreadyAdded', { member: name }),
        );
      },
    });
  }

  function handleRemoveMember(userId: string): void {
    const name = memberName(userId);
    clearMemberError(userId);
    removeMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          announce(t('boardMenu.announce.memberRemoved', { member: name }));
        },
        // ⚠ State only, never `announce()`: `useRemoveBoardMember` already
        // announces `error.message` itself (trap 4). This is the *visible*
        // explanation `FR-PERM-032` asks for, on the row it happened to.
        onError: (error) => {
          recordMemberError(userId, error.message);
        },
      },
    );
  }

  function closeShareSection(): void {
    setSection('root');
    const restoreFocus = () => shareBoardTriggerRef.current?.focus();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(restoreFocus);
    } else {
      restoreFocus();
    }
  }

  function handleChangeRole(userId: string, nextRole: BoardRole): void {
    const name = memberName(userId);
    clearMemberError(userId);
    roleMutation.mutate(
      { userId, role: nextRole },
      {
        onSuccess: (updated) => {
          announce(t(ROLE_ANNOUNCEMENT_KEYS[updated.role], { member: name }));
        },
        onError: (error) => {
          recordMemberError(userId, error.message);
        },
      },
    );
  }

  useEffect(() => {
    if (open) {
      if (initialSection !== undefined) setSection(initialSection);
      // Non-modal, so this is a *move*, not a trap: nothing stops the next
      // Tab or a click leaving for the board face again. The `<aside>` itself
      // rather than its first control, so a screen reader reads the landmark
      // and its label before the entries.
      panelRef.current?.focus();
      wasOpen.current = true;
      return;
    }

    // A reopened panel starts at the root and confirms nothing — the section
    // stack is panel-scoped state, not board state.
    setSection('root');
    setConfirming(null);
    setCopyOpen(false);
    setCopyResult(null);
    setSnapshotRestoreResult(null);
    setLeaveConfirmOpen(false);
    setLeaveError(null);
    setAutomationRowErrors({});
    setAutomationAdminRowErrors({});
    setAutomationDuplicateResult(null);
    setAutomationLogTarget(null);
    setAutomationLogOlder(null);
    setAutomationAdminDefinitionTarget(null);
    setPowerUpQuery('');
    setPowerUpSearch('');
    setPowerUpCategory('all');
    setPowerUpPricingModel('all');
    setPowerUpMadeByUs(false);
    setPowerUpDetailId(null);
    setPowerUpRowErrors({});
    // Row-scoped refusals are panel-scoped state too: a last-admin 409 must
    // not be waiting on a row when the panel is opened again minutes later.
    setMemberErrors({});
    // The same fact about the two refusals this file does NOT hold itself.
    // `addMutation.error` and `visibilityMutation.error` live in React Query
    // and outlive a closed panel — and unlike close/delete's, both of them are
    // *rendered*, so a stale one would greet the next opener with a sentence
    // about a request they did not make. `reset` is stable in v5.
    resetAdd();
    resetBoardSilence();
    resetVisibility();
    resetMemberPermission();
    resetCommentingPermission();
    resetSelfJoin();
    resetVotingEnabled();
    resetVotingPermission();
    resetVotingHideTallies();
    resetCardAgingMode();
    resetCardNumbers();
    resetCardKey();
    resetCreateInviteLink();
    resetRevokeInviteLink();
    resetRegenerateEmailToBoard();
    resetUpdateEmailToBoardSettings();
    resetGenerateCalendarFeedToken();
    resetUpdateCalendarFeed();
    resetSetSnapshotsEnabled();
    resetSetSnapshotRetention();
    resetSetSnapshotCaptureAttachments();
    resetRestoreSnapshot();
    resetCreateAutomation();
    resetUpdateAutomation();
    resetToggleAutomation();
    resetDeleteAutomation();
    resetDuplicateAutomation();
    resetAdminDisableAutomation();
    resetEnablePowerUp();
    resetUpdatePowerUpHeader();
    resetUpdatePowerUpSettings();
    resetAuthorizePowerUp();
    resetRevokePowerUpAuthorization();
    resetCreateCustomField();
    resetRenameCustomField();
    resetHiddenCustomField();
    resetShowOnFrontCustomField();
    resetMoveCustomField();
    resetDeleteCustomField();
    resetCreateCustomFieldOption();
    resetRenameCustomFieldOption();
    resetMoveCustomFieldOption();
    resetDeleteCustomFieldOption();
    resetCopyBoard();
    resetTemplateMark();
    resetTemplateUnmark();
    resetTemplateDescription();

    if (wasOpen.current) {
      wasOpen.current = false;
      // `FR-EXP-022` AC3. After a delete the trigger has been unmounted with
      // the board face; focusing a detached node is a no-op, which is the
      // right outcome — the host is navigating away.
      returnFocusRef.current?.focus();
    }
  }, [
    open,
    initialSection,
    resetAdd,
    resetBoardSilence,
    resetCommentingPermission,
    resetCreateCustomField,
    resetCreateCustomFieldOption,
    resetCreateInviteLink,
    resetDeleteCustomField,
    resetDeleteCustomFieldOption,
    resetGenerateCalendarFeedToken,
    resetHiddenCustomField,
    resetCopyBoard,
    resetMemberPermission,
    resetMoveCustomField,
    resetMoveCustomFieldOption,
    resetRegenerateEmailToBoard,
    resetRenameCustomField,
    resetRenameCustomFieldOption,
    resetRevokeInviteLink,
    resetSelfJoin,
    resetShowOnFrontCustomField,
    resetTemplateDescription,
    resetTemplateMark,
    resetTemplateUnmark,
    resetUpdateCalendarFeed,
    resetRestoreSnapshot,
    resetSetSnapshotCaptureAttachments,
    resetSetSnapshotRetention,
    resetSetSnapshotsEnabled,
    resetCreateAutomation,
    resetUpdateAutomation,
    resetToggleAutomation,
    resetDeleteAutomation,
    resetDuplicateAutomation,
    resetAdminDisableAutomation,
    resetAuthorizePowerUp,
    resetUpdateEmailToBoardSettings,
    resetEnablePowerUp,
    resetVisibility,
    resetCardAgingMode,
    resetCardNumbers,
    resetCardKey,
    resetRevokePowerUpAuthorization,
    resetUpdatePowerUpHeader,
    resetUpdatePowerUpSettings,
    resetVotingEnabled,
    resetVotingHideTallies,
    resetVotingPermission,
    returnFocusRef,
  ]);

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>): void {
    if (event.key !== 'Escape') return;
    // ⚠ The dismissal ladder. Radix's `DismissableLayer` handles Escape on the
    // *document, in the capture phase*, and marks the event
    // `defaultPrevented` when it dismisses — so an Escape that has already
    // closed `BoardDangerConfirm` must not also close the panel. The confirm
    // is rendered outside this `<aside>` so it does not reach this handler at
    // all today; the guard makes that a property of the code rather than of
    // the current JSX shape.
    if (event.defaultPrevented) return;
    event.preventDefault();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    if (confirming === 'close') {
      // No announcement on success: `BoardClosedNotice` appearing on the board
      // face is that event's one channel (§5.7).
      closeMutation.mutate(undefined, { onSuccess: () => setConfirming(null) });
      return;
    }

    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        // §5.7: the board face is about to be gone and cannot announce
        // anything, so this is the one place the delete can be announced from.
        announce(t('boardMenu.deleted', { board: board.title }));
        setConfirming(null);
        onOpenChange(false);
        onDeleted();
      },
    });
  }

  function handleOpenLeave(): void {
    setLeaveError(null);
    removeMutation.reset();
    setLeaveConfirmOpen(true);
  }

  function handleLeaveBoard(): void {
    if (profile?.id === undefined) return;
    const userId = profile.id;
    setLeaveError(null);
    removeMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          announce(t('boardMenu.left', { board: board.title }));
          setLeaveConfirmOpen(false);
          onOpenChange(false);
          onLeft();
        },
        onError: (error) => {
          setLeaveError(error.message);
        },
      },
    );
  }

  if (!open) return null;

  const busy =
    confirming === 'close' ? closeMutation.isPending : deleteMutation.isPending;
  const busyCustomFieldIds = [
    renameCustomFieldMutation.isPending
      ? renameCustomFieldMutation.variables?.fieldId
      : undefined,
    hiddenCustomFieldMutation.isPending
      ? hiddenCustomFieldMutation.variables?.fieldId
      : undefined,
    showOnFrontCustomFieldMutation.isPending
      ? showOnFrontCustomFieldMutation.variables?.fieldId
      : undefined,
    moveCustomFieldMutation.isPending
      ? moveCustomFieldMutation.variables?.fieldId
      : undefined,
    deleteCustomFieldMutation.isPending
      ? deleteCustomFieldMutation.variables?.fieldId
      : undefined,
    createCustomFieldOptionMutation.isPending
      ? createCustomFieldOptionMutation.variables?.fieldId
      : undefined,
    renameCustomFieldOptionMutation.isPending
      ? renameCustomFieldOptionMutation.variables?.fieldId
      : undefined,
    moveCustomFieldOptionMutation.isPending
      ? moveCustomFieldOptionMutation.variables?.fieldId
      : undefined,
    deleteCustomFieldOptionMutation.isPending
      ? deleteCustomFieldOptionMutation.variables?.fieldId
      : undefined,
  ].filter((fieldId): fieldId is string => fieldId !== undefined);
  const busyCustomFieldOptionIds = [
    renameCustomFieldOptionMutation.isPending
      ? renameCustomFieldOptionMutation.variables?.optionId
      : undefined,
    moveCustomFieldOptionMutation.isPending
      ? moveCustomFieldOptionMutation.variables?.optionId
      : undefined,
    deleteCustomFieldOptionMutation.isPending
      ? deleteCustomFieldOptionMutation.variables?.optionId
      : undefined,
  ].filter((optionId): optionId is string => optionId !== undefined);
  const selectedPowerUpInstall =
    powerUpDetailId === null
      ? undefined
      : boardPowerUps.data?.powerUps.find(
          (entry) => entry.powerUp.id === powerUpDetailId,
        );
  const selectedPowerUpInstallKnown =
    powerUpDetailId !== null && boardPowerUps.data !== undefined;

  return (
    <>
      <aside
        ref={panelRef}
        /*
         * Explicit, not implicit: `<aside>` maps to `complementary` only while
         * it is not nested in sectioning content, and where this mounts is the
         * host's decision (see the layout contract above). Written out so the
         * landmark cannot be lost by a wrapper somebody else adds.
         */
        role="complementary"
        aria-label={t('boardMenu.landmark', { board: board.title })}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex h-full max-h-full w-80 shrink-0 flex-col gap-3 overflow-y-auto border-l border-border-strong bg-surface p-3 text-ink',
          mobile && 'fixed inset-y-0 right-0 z-40 w-[min(20rem,100vw)] shadow-elevated',
        )}
      >
        <div className="flex items-center gap-2">
          {/*
            One control for all three sub-sections, `D4` included: Back returns
            to `'root'` **without closing** (AC2), and it is the same real
            `<button>` `D3` shipped rather than a per-section copy.
          */}
          {section !== 'root' && (
            <Button variant="quiet" size="sm" onClick={() => setSection('root')}>
              {t('boardMenu.back')}
            </Button>
          )}
          <h2 className="min-w-0 flex-1 font-display text-base font-semibold">
            {t(boardMenuMessageKey(section))}
          </h2>
          <IconButton
            label={t('boardMenu.hide')}
            variant="quiet"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            <CloseGlyph />
          </IconButton>
        </div>

        {section === 'root' && (
          <>
            <BoardCapacityNotice
              boardTitle={board.title}
              capacity={capacity.data}
              compact
            />

            <div className="flex flex-col gap-1">
              {/*
                `D4`'s two entries, above the `<hr>` and the danger zone —
                §6.7. Routine entries, so plain `quiet` `Button`s in the same
                group as *Archived items*; neither is gated on a capability,
                because both sub-sections render for every reader (the
                *controls* inside them lock, §2.1).
              */}
              {!isTemplate && (
                <Button
                  variant="quiet"
                  className="w-full justify-start"
                  onClick={() => setSection('activity')}
                >
                  {t('boardMenu.activity')}
                </Button>
              )}
              {onOpenShortcuts !== undefined ? (
                <Button
                  variant="quiet"
                  className="w-full justify-between"
                  aria-label={t('boardMenu.keyboardShortcuts')}
                  onClick={onOpenShortcuts}
                >
                  {t('boardMenu.keyboardShortcutsVisible')}
                </Button>
              ) : null}
              <div className="px-1 py-2">
                <WatchToggle
                  target={{ targetType: 'board', targetId: board.id }}
                  label={t('boardMenu.watch.label')}
                  description={t('boardMenu.watch.description')}
                  enabled={sessionReadyOpen}
                />
              </div>
              <div className="rounded-card bg-surface px-3 py-2">
                <label className="flex min-h-8 items-center justify-between gap-3 text-sm text-ink">
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {t('boardMenu.silence.label')}
                    </span>
                    <span className="block text-xs text-ink-muted">
                      {t('boardMenu.silence.description')}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    className="size-4 shrink-0 accent-accent"
                    checked={boardSilence.data?.silenced === true}
                    disabled={
                      boardSilence.isPending ||
                      boardSilence.isError ||
                      boardSilenceMutation.isPending ||
                      profile === null
                    }
                    aria-label={
                      boardSilence.data?.silenced === true
                        ? t('boardMenu.silence.onLabel')
                        : t('boardMenu.silence.offLabel')
                    }
                    onChange={(event) =>
                      handleChangeBoardSilence(event.currentTarget.checked)
                    }
                  />
                </label>
                {boardSilence.isError || boardSilenceMutation.isError ? (
                  <p className="mt-1 text-xs text-bad">
                    {boardSilence.error?.message ??
                      boardSilenceMutation.error?.message ??
                      t('boardMenu.silence.saveFailed')}
                  </p>
                ) : null}
              </div>
              <Button
                variant="quiet"
                className="w-full justify-start"
                disabled={onCollapseAllLists === undefined || lists.length === 0}
                disabledReason={
                  onCollapseAllLists === undefined
                    ? t('boardMenu.reason.signInCollapseLists')
                    : lists.length === 0
                      ? t('boardMenu.reason.noListsToCollapse')
                      : undefined
                }
                onClick={() => onCollapseAllLists?.()}
              >
                {t('boardMenu.collapseAllLists')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                disabled={onExpandAllLists === undefined || lists.length === 0}
                disabledReason={
                  onExpandAllLists === undefined
                    ? t('boardMenu.reason.signInExpandLists')
                    : lists.length === 0
                      ? t('boardMenu.reason.noListsToExpand')
                      : undefined
                }
                onClick={() => onExpandAllLists?.()}
              >
                {t('boardMenu.expandAllLists')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('members')}
              >
                {t('boardMenu.members')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('invite-link')}
                disabled={!capabilities.canManageMembers}
                disabledReason={capabilities.manageMembersDisabledReason ?? undefined}
              >
                {t('boardMenu.inviteLink')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('settings')}
              >
                {t('boardMenu.settings')}
              </Button>
              <Button
                ref={shareBoardTriggerRef}
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('share')}
              >
                {t('boardMenu.share')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('background')}
              >
                {t('boardMenu.background')}
              </Button>
              {/*
                ⚠ ONE KEY FOR BOTH NODES, on the `boardMenu.background`
                precedent. This entry and the `<h2>` `SECTION_HEADING_KEYS`
                renders both resolve `boardMenu.stickers`, so two nodes in this
                panel carry the string *Stickers* and `getByText('Stickers')`
                is a strict-mode violation rather than a miss. Every selector
                for either is by ROLE — `getByRole('button', …)` for this one
                and `getByRole('heading', { level: 2, … })` for the heading —
                exactly as `FR-EXP-025.spec.ts:203`/`:204` disambiguate
                *Change background*. Declaring a second key to dodge that would
                give a translator two words for one noun.
              */}
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('stickers')}
              >
                {t('boardMenu.stickers')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('custom-fields')}
              >
                {t('boardMenu.customFields')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('integrations')}
              >
                {t('boardMenu.integrationsShort')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => {
                  setPowerUpDetailId(null);
                  setSection('power-ups');
                }}
              >
                {t('boardMenu.powerUps')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('automation')}
                disabled={!canViewAutomation}
                disabledReason={automationSectionDisabledReason ?? undefined}
              >
                {t('boardMenu.automation')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('snapshots')}
                disabled={!canManageSnapshots}
                disabledReason={snapshotsDisabledReason ?? undefined}
              >
                {t('boardMenu.snapshots')}
              </Button>
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('external-access')}
                disabled={!canUseEmailToBoard && !canUseCalendarFeed}
                disabledReason={
                  !canUseEmailToBoard && !canUseCalendarFeed
                    ? (calendarFeedDisabledReason ??
                      emailToBoardDisabledReason ??
                      undefined)
                    : undefined
                }
              >
                {t('boardMenu.externalAccess')}
              </Button>
              {copyBoardDisabledReason === null ? (
                <Button
                  variant="quiet"
                  className="w-full justify-start"
                  onClick={() => {
                    setCopyResult(null);
                    setCopyOpen(true);
                  }}
                >
                  {t('boardMenu.copyBoard')}
                </Button>
              ) : (
                <Button
                  variant="quiet"
                  className="w-full justify-start"
                  disabled
                  disabledReason={copyBoardDisabledReason}
                >
                  {t('boardMenu.copyBoard')}
                </Button>
              )}
              <Button
                variant="quiet"
                className="w-full justify-start"
                onClick={() => setSection('archived')}
              >
                {t('boardMenu.archived')}
              </Button>
            </div>

            <TemplateDescriptionSection
              template={currentTemplate}
              canEdit={canManageTemplate}
              busy={descriptionMutation.isPending}
              error={descriptionMutation.error?.message}
              onSave={handleSaveTemplateDescription}
            />

            {isTemplate &&
            currentTemplate !== null &&
            onChangeTemplateVisibility !== undefined &&
            onChangeTemplateCategory !== undefined ? (
              <TemplateVisibilityControls
                template={currentTemplate}
                canManage={canManageTemplate}
                disabledReason={templateDisabledReason}
                visibilityBusy={templateVisibilityBusy}
                categoryBusy={templateCategoryBusy}
                visibilityError={templateVisibilityError}
                categoryError={templateCategoryError}
                onChangeVisibility={onChangeTemplateVisibility}
                onChangeCategory={onChangeTemplateCategory}
              />
            ) : null}

            {!isTemplate &&
              (makeTemplateActionDisabledReason === null ? (
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  busy={markTemplateMutation.isPending}
                  onClick={handleMakeTemplate}
                >
                  {t('boardMenu.makeTemplate')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  disabled
                  disabledReason={makeTemplateActionDisabledReason}
                >
                  {t('boardMenu.makeTemplate')}
                </Button>
              ))}

            {isTemplate && templateActionDisabledReason === null && (
              <Button
                variant="secondary"
                className="w-full justify-start"
                busy={unmarkTemplateMutation.isPending}
                onClick={handleRemoveTemplateFlag}
              >
                {t('boardMenu.removeTemplateFlag')}
              </Button>
            )}

            {/*
              `FR-BOARD-005`'s first edge case: destructive entries are
              *visually separated* from routine ones. The separator and the
              labelled group are the separation; the ink is not.
            */}
            <hr className="border-border" />

            <div
              role="group"
              aria-labelledby={dangerHeadingId}
              className="flex flex-col gap-1"
            >
              <h3
                id={dangerHeadingId}
                className="px-1 pb-1 text-sm font-semibold text-ink-muted"
              >
                {t('boardMenu.dangerZone')}
              </h3>

              {/* §2.3, first axis: offered while the board is OPEN. */}
              {closeOffered && (
                <LifecycleEntry
                  label={t('boardMenu.closeBoard')}
                  reason={capabilities.closeDisabledReason}
                  onSelect={() => setConfirming('close')}
                />
              )}

              {/*
                §2.3, the other axis: `FR-EXP-012` AC4 — absent while the board
                is open, at every role, admin included.
              */}
              {capabilities.deleteOffered && (
                <LifecycleEntry
                  label={t('boardMenu.deleteBoard')}
                  reason={capabilities.deleteDisabledReason}
                  onSelect={() => setConfirming('delete')}
                />
              )}

              <LifecycleEntry
                label={t('boardMenu.leaveBoard')}
                reason={leaveDisabledReason}
                onSelect={handleOpenLeave}
              />
            </div>
          </>
        )}

        {section === 'activity' && (
          <BoardActivitySection boardId={board.id} onOpenCard={onOpenCard} />
        )}

        {section === 'members' && (
          /*
            `FE-10.2`. Presentational — this file owns every query and every
            mutation, which is the house pattern (`WorkspaceMemberList` +
            `w.$workspaceId.members.tsx`).

            ⚠ `members.data ?? []` renders an empty `<ul>` for the width of the
            request and **no `EmptyState`**: a board always has at least one
            member (its admin), so the empty case is unreachable rather than
            unhandled (§8). The list fills in when the query lands.
          */
          <div className="flex flex-col gap-4">
            {mobile ? (
              <MobileParityNotice
                kind="workspace-admin"
                title={t('boardMenu.mobile.workspaceAdmin.title')}
                body={t('boardMenu.mobile.workspaceAdmin.accessBody')}
                href={mobileBoardHref}
              />
            ) : null}
            <BoardMemberList
              members={members.data ?? []}
              viewerId={profile?.id}
              canManageMembers={capabilities.canManageMembers}
              canChangeRoles={canChangeRoles}
              onAddMember={handleAddMember}
              onRemoveMember={handleRemoveMember}
              onChangeRole={handleChangeRole}
              addBusy={addMutation.isPending}
              busyMemberId={busyMemberId}
              addError={addMutation.error?.message}
              memberErrors={memberErrors}
            />
          </div>
        )}

        {section === 'invite-link' && (
          <BoardInviteLinkSection
            link={inviteLink.data}
            loading={inviteLink.isPending}
            canManage={canManageInviteLink}
            disabledReason={capabilities.manageMembersDisabledReason}
            showHeading={false}
            createBusy={createInviteLinkMutation.isPending}
            revokeBusy={revokeInviteLinkMutation.isPending}
            createError={createInviteLinkMutation.error?.message}
            revokeError={revokeInviteLinkMutation.error?.message}
            origin={typeof window === 'undefined' ? '' : window.location.origin}
            onCreate={handleCreateInviteLink}
            onRevoke={handleRevokeInviteLink}
          />
        )}

        {section === 'settings' && (
          <div className="flex flex-col gap-4">
            {mobile ? (
              <MobileParityNotice
                kind="workspace-admin"
                title={t('boardMenu.mobile.workspaceAdmin.title')}
                body={t('boardMenu.mobile.workspaceAdmin.policyBody')}
                href={mobileBoardHref}
              />
            ) : null}
            <BoardSettingsSection
              visibility={board.visibility}
              canChangeVisibility={capabilities.canChangeVisibility}
              visibilityDisabledReason={capabilities.visibilityDisabledReason}
              onChangeVisibility={handleChangeVisibility}
              visibilityBusy={visibilityMutation.isPending}
              visibilityError={visibilityMutation.error?.message}
              offerEnterpriseVisibility={offerEnterpriseVisibility}
              canChangePermissions={canChangePermissionSettings}
              permissionDisabledReason={permissionSettingsDisabledReason}
              memberPermission={memberPermission.data?.memberAddRemovePermission}
              commentingPermission={commentingPermission.data?.commentingPermission}
              selfJoinEnabled={selfJoin.data?.workspaceSelfJoinEnabled}
              votingEnabled={votingEnabled.data?.votingEnabled}
              votingPermission={votingPermission.data?.votingPermission}
              votingHideTallies={votingHideTallies.data?.votingHideTallies}
              cardAgingMode={cardAgingMode.data?.cardAgingMode}
              onChangeMemberPermission={handleChangeMemberPermission}
              onChangeCommentingPermission={handleChangeCommentingPermission}
              onChangeSelfJoin={handleChangeSelfJoin}
              onChangeVotingEnabled={handleChangeVotingEnabled}
              onChangeVotingPermission={handleChangeVotingPermission}
              onChangeVotingHideTallies={handleChangeVotingHideTallies}
              onChangeCardAgingMode={handleChangeCardAgingMode}
              memberPermissionBusy={memberPermissionMutation.isPending}
              commentingPermissionBusy={commentingPermissionMutation.isPending}
              selfJoinBusy={selfJoinMutation.isPending}
              votingEnabledBusy={votingEnabledMutation.isPending}
              votingPermissionBusy={votingPermissionMutation.isPending}
              votingHideTalliesBusy={votingHideTalliesMutation.isPending}
              cardAgingModeBusy={cardAgingModeMutation.isPending}
              memberPermissionError={memberPermissionMutation.error?.message}
              commentingPermissionError={commentingPermissionMutation.error?.message}
              selfJoinError={selfJoinMutation.error?.message}
              votingEnabledError={votingEnabledMutation.error?.message}
              votingPermissionError={votingPermissionMutation.error?.message}
              votingHideTalliesError={votingHideTalliesMutation.error?.message}
              cardAgingModeError={cardAgingModeMutation.error?.message}
              showCardNumbersOnFront={board.showCardNumbersOnFront}
              onChangeShowCardNumbers={handleChangeShowCardNumbers}
              showCardNumbersBusy={cardNumbersMutation.isPending}
              showCardNumbersError={cardNumbersMutation.error?.message}
              requiredApprovals={board.requiredApprovals}
              onSaveRequiredApprovals={handleSaveRequiredApprovals}
              requiredApprovalsBusy={requiredApprovalsMutation.isPending}
              requiredApprovalsError={requiredApprovalsMutation.error?.message}
              dangerousComments={board.dangerousComments}
              onSaveDangerousComments={handleSaveDangerousComments}
              dangerousCommentsBusy={dangerousCommentsMutation.isPending}
              dangerousCommentsError={dangerousCommentsMutation.error?.message}
              cardKey={board.cardKey}
              boardTitle={board.title}
              onSaveCardKey={handleSaveCardKey}
              cardKeyBusy={cardKeyMutation.isPending}
              cardKeyTaken={isCardKeyTaken(cardKeyMutation.error)}
              cardKeyError={cardKeyMutation.error?.message}
            />
          </div>
        )}

        {section === 'background' && (
          <BoardBackgroundPicker
            boardId={board.id}
            background={board.background}
            canChange={canChangeBackground}
            disabledReason={backgroundDisabledReason}
            onChange={handleChangeBackground}
            customBackgrounds={customBackgrounds.data}
            customBackgroundsLoading={customBackgrounds.isPending}
            onUploadCustomBackground={handleUploadCustomBackground}
            onApplyCustomBackground={handleApplyCustomBackground}
            busy={
              backgroundMutation.isPending ||
              uploadCustomBackgroundMutation.isPending ||
              applyCustomBackgroundMutation.isPending
            }
            error={
              backgroundMutation.error?.message ??
              uploadCustomBackgroundMutation.error?.message ??
              applyCustomBackgroundMutation.error?.message
            }
          />
        )}

        {/*
          `FE-58.1` — the tray `FR-CARD-032` AC1 and `FR-EXP-027` AC1 both put
          in the board menu in their own behaviour text, rather than as
          persistent board-face chrome. AUDIT.md §8: it costs the board face
          **0px** against a 2.4px density headroom, because the panel is
          already mounted and already scrolls.

          THE PROP CONTRACT, written out because it crosses two files and this
          repository's recorded defect is the one that does not:

            boardId        string             — the tray reads
                                                `GET /boards/:id/stickers` and
                                                writes
                                                `POST /cards/:id/stickers`;
                                                both are shipped routes.
            lists          readonly BoardFaceList[]
                                              — every list with its cards, so
                                                the KEYBOARD placement path
                                                (Principle 5: choose a sticker,
                                                choose a card, place) can name a
                                                card without a second query.
                                                The same array the email
                                                destination picker already
                                                receives.
            canPlace       boolean            — the EDITOR gate computed above.
                                                Reading is ungated; placing is
                                                not.
            disabledReason string | null      — the sentence beside a refused
                                                control, `null` when it may be
                                                used. The `BoardBackgroundPicker`
                                                `canChange`/`disabledReason`
                                                pair exactly.

          All four are passed as PLAIN props, never a conditional JSX spread —
          a spread bypasses excess-property checking, so it compiles against
          props that were never declared (the recorded C2 defect).
        */}
        {section === 'stickers' && (
          <BoardStickerTray
            boardId={board.id}
            lists={lists}
            canPlace={canPlaceStickers}
            disabledReason={stickersDisabledReason}
          />
        )}

        {section === 'custom-fields' && (
          <div className="flex flex-col gap-4">
            {mobile ? (
              <MobileParityNotice
                kind="custom-fields"
                title={t('boardMenu.mobile.customFields.title')}
                body={customFieldMobileReason}
                href={mobileBoardHref}
              />
            ) : null}
            <BoardCustomFieldsSection
              definitions={customFields.data}
              loading={customFields.isPending}
              canManage={canManageCustomFields && !mobile}
              disabledReason={
                mobile && canManageCustomFields ? null : customFieldsDisabledReason
              }
              createBusy={createCustomFieldMutation.isPending}
              busyDefinitionIds={busyCustomFieldIds}
              busyOptionIds={busyCustomFieldOptionIds}
              error={customFields.error?.message}
              onCreateDefinition={(body) =>
                createCustomFieldMutation.mutate(
                  { boardId: board.id, body },
                  {
                    onSuccess: (definition) =>
                      announce(
                        t('boardMenu.customFields.created', {
                          field: definition.name,
                        }),
                      ),
                  },
                )
              }
              onRenameDefinition={(fieldId, name) =>
                renameCustomFieldMutation.mutate(
                  { boardId: board.id, fieldId, name },
                  { onSuccess: () => announce(t('boardMenu.customFields.renamed')) },
                )
              }
              onToggleHidden={(fieldId, hidden) =>
                hiddenCustomFieldMutation.mutate(
                  { boardId: board.id, fieldId, hidden },
                  {
                    onSuccess: () =>
                      announce(
                        hidden
                          ? t('boardMenu.customFields.hidden')
                          : t('boardMenu.customFields.shown'),
                      ),
                  },
                )
              }
              onToggleShowOnFront={(fieldId, showOnFront) =>
                showOnFrontCustomFieldMutation.mutate(
                  { boardId: board.id, fieldId, showOnFront },
                  {
                    onSuccess: () =>
                      announce(
                        showOnFront
                          ? t('boardMenu.customFields.showOnFront')
                          : t('boardMenu.customFields.hideOnFront'),
                      ),
                  },
                )
              }
              onDeleteDefinition={(fieldId) =>
                deleteCustomFieldMutation.mutate(
                  { boardId: board.id, fieldId },
                  { onSuccess: () => announce(t('boardMenu.customFields.deleted')) },
                )
              }
              onReorderDefinition={(fieldId, patch) =>
                moveCustomFieldMutation.mutate(
                  {
                    boardId: board.id,
                    fieldId,
                    previousDefinitionId: patch.previousDefinitionId,
                    nextDefinitionId: patch.nextDefinitionId,
                  },
                  { onSuccess: () => announce(t('boardMenu.customFields.reordered')) },
                )
              }
              onAddOption={(fieldId, label) =>
                createCustomFieldOptionMutation.mutate(
                  { boardId: board.id, fieldId, label },
                  { onSuccess: () => announce(t('boardMenu.customFields.optionAdded')) },
                )
              }
              onRenameOption={(optionId, label) => {
                const definition = customFields.data?.find((candidate) =>
                  candidate.options.some((option) => option.id === optionId),
                );
                if (definition === undefined) return;
                renameCustomFieldOptionMutation.mutate(
                  { boardId: board.id, fieldId: definition.id, optionId, label },
                  {
                    onSuccess: () => announce(t('boardMenu.customFields.optionRenamed')),
                  },
                );
              }}
              onDeleteOption={(optionId) => {
                const definition = customFields.data?.find((candidate) =>
                  candidate.options.some((option) => option.id === optionId),
                );
                if (definition === undefined) return;
                deleteCustomFieldOptionMutation.mutate(
                  { boardId: board.id, fieldId: definition.id, optionId },
                  {
                    onSuccess: () => announce(t('boardMenu.customFields.optionDeleted')),
                  },
                );
              }}
              onReorderOption={(optionId, patch) => {
                const definition = customFields.data?.find((candidate) =>
                  candidate.options.some((option) => option.id === optionId),
                );
                if (definition === undefined) return;
                moveCustomFieldOptionMutation.mutate(
                  {
                    boardId: board.id,
                    fieldId: definition.id,
                    optionId,
                    previousOptionId: patch.previousOptionId,
                    nextOptionId: patch.nextOptionId,
                  },
                  {
                    onSuccess: () =>
                      announce(t('boardMenu.customFields.optionReordered')),
                  },
                );
              }}
            />
          </div>
        )}

        {section === 'automation' && (
          <div className="flex flex-col gap-4">
            {mobile ? (
              <MobileParityNotice
                kind="automation"
                title={t('boardMenu.mobile.automation.title')}
                body={t('boardMenu.mobile.automation.body')}
                href={mobileBoardHref}
              />
            ) : null}
            <BoardAutomationSection
              boardId={board.id}
              currentUserId={profile?.id ?? null}
              lists={lists}
              labels={automationLabels.data}
              members={members.data}
              activityMembers={workspaceMembers.data}
              canManage={canManageAutomation}
              disabledReason={automationDisabledReason}
              readOnly={mobile && canManageAutomation}
              readOnlyReason={t('boardMenu.mobile.automation.bodyShort')}
              commands={automations.data?.commands}
              commandsLoading={canManageAutomation && automations.isPending}
              labelsLoading={automationLabels.isPending}
              membersLoading={members.isPending}
              destinationBoards={automationDestinationBoards.data}
              createBusy={createAutomationMutation.isPending}
              updateBusy={updateAutomationMutation.isPending}
              toggleBusyId={
                toggleAutomationMutation.isPending
                  ? (toggleAutomationMutation.variables?.commandId ?? null)
                  : null
              }
              deleteBusyId={
                deleteAutomationMutation.isPending
                  ? (deleteAutomationMutation.variables?.commandId ?? null)
                  : null
              }
              duplicateBusyId={
                duplicateAutomationMutation.isPending
                  ? (duplicateAutomationMutation.variables?.commandId ?? null)
                  : null
              }
              duplicateResult={automationDuplicateResult}
              logCommand={automationLogTarget}
              log={automationLog.data ?? null}
              logLoading={automationLogTarget !== null && automationLog.isPending}
              logError={automationLog.error?.message}
              olderLoading={automationLogOlderForTarget?.loading ?? false}
              olderError={automationLogOlderForTarget?.error ?? undefined}
              activity={automationActivity.data ?? null}
              activityLoading={automationActivity.isPending}
              activityError={automationActivity.error?.message}
              activityBoardId={automationActivityBoardId}
              activityOutcome={automationActivityOutcome}
              usage={automationUsage.data ?? null}
              usageLoading={automationUsage.isPending}
              usageError={automationUsage.error?.message}
              canAdminReview={canAdminReviewAutomation}
              adminDisabledReason={automationAdminDisabledReason}
              adminReview={automationAdminReview.data ?? null}
              adminReviewLoading={automationAdminReview.isPending}
              adminReviewError={automationAdminReview.error?.message}
              adminBoardId={automationAdminBoardId}
              adminDisableBusyId={
                adminDisableAutomationMutation.isPending
                  ? (adminDisableAutomationMutation.variables?.commandId ?? null)
                  : null
              }
              adminDefinitionTarget={automationAdminDefinitionTarget}
              adminDefinition={automationAdminDefinition.data ?? null}
              adminDefinitionLoading={
                automationAdminDefinitionTarget !== null &&
                automationAdminDefinition.isPending
              }
              adminDefinitionError={automationAdminDefinition.error?.message}
              error={canManageAutomation ? automations.error?.message : undefined}
              editorError={
                createAutomationMutation.error?.message ??
                updateAutomationMutation.error?.message
              }
              rowErrors={automationRowErrors}
              adminRowErrors={automationAdminRowErrors}
              onCreate={handleCreateAutomation}
              onUpdate={handleUpdateAutomation}
              onDelete={handleDeleteAutomation}
              onToggleEnabled={handleToggleAutomation}
              onDuplicate={handleDuplicateAutomation}
              onOpenLog={handleOpenAutomationLog}
              onCloseLog={handleCloseAutomationLog}
              onLoadOlder={handleLoadOlderAutomationLog}
              onActivityBoardChange={setAutomationActivityBoardId}
              onActivityOutcomeChange={setAutomationActivityOutcome}
              onAdminBoardChange={setAutomationAdminBoardId}
              onOpenAdminDefinition={setAutomationAdminDefinitionTarget}
              onCloseAdminDefinition={() => setAutomationAdminDefinitionTarget(null)}
              onAdminDisable={handleAdminDisableAutomation}
              onRetryActivity={() => void automationActivity.refetch()}
              onRetryUsage={() => void automationUsage.refetch()}
              onRetryAdminReview={() => void automationAdminReview.refetch()}
              onRetryAdminDefinition={() => void automationAdminDefinition.refetch()}
              onClearDuplicateResult={() => {
                setAutomationDuplicateResult(null);
                resetDuplicateAutomation();
              }}
            />
          </div>
        )}

        {section === 'integrations' && (
          <FirstPartyIntegrationPanel
            integrations={firstPartyIntegrations.data?.integrations}
            loading={firstPartyIntegrations.isPending}
            loadError={firstPartyIntegrations.error?.message}
            canManage={canManageIntegrations}
            disabledReason={integrationsDisabledReason}
            rowErrors={integrationRowErrors}
            onConnect={(key) =>
              handleUnavailableIntegrationAction(key, t('boardMenu.integration.connect'))
            }
            onReconnect={(key) =>
              handleUnavailableIntegrationAction(
                key,
                t('boardMenu.integration.reconnect'),
              )
            }
            onDisconnect={(key) =>
              handleUnavailableIntegrationAction(
                key,
                t('boardMenu.integration.disconnect'),
              )
            }
            onConfigure={(key) =>
              handleUnavailableIntegrationAction(
                key,
                t('boardMenu.integration.configure'),
              )
            }
            onEnableOnBoard={handleEnableFirstPartyIntegration}
            onDisableOnBoard={handleDisableFirstPartyIntegration}
            onRetry={() => void firstPartyIntegrations.refetch()}
          />
        )}

        {section === 'power-ups' &&
          (powerUpDetailId === null ? (
            <div className="flex flex-col gap-5">
              {mobile ? (
                <MobileParityNotice
                  kind="power-ups"
                  title={t('boardMenu.mobile.powerUps.title')}
                  body={powerUpMobileReason}
                  href={mobileBoardHref}
                />
              ) : null}
              <BoardPowerUpList
                entries={boardPowerUps.data?.powerUps ?? []}
                loading={boardPowerUps.isPending}
                error={boardPowerUps.error?.message}
                canManage={canManagePowerUps && !mobile}
                manageDisabledReason={
                  mobile && canManagePowerUps ? null : powerUpsDisabledReason
                }
                visibilityBusyId={
                  updatePowerUpHeaderMutation.isPending
                    ? (updatePowerUpHeaderMutation.variables?.boardPowerUpId ?? null)
                    : null
                }
                settingsBusyId={
                  updatePowerUpSettingsMutation.isPending
                    ? (updatePowerUpSettingsMutation.variables?.boardPowerUpId ?? null)
                    : null
                }
                authorizationBusyId={
                  authorizePowerUpMutation.isPending
                    ? (authorizePowerUpMutation.variables?.boardPowerUpId ?? null)
                    : revokePowerUpAuthorizationMutation.isPending
                      ? (revokePowerUpAuthorizationMutation.variables?.boardPowerUpId ??
                        null)
                      : null
                }
                disableBusyId={
                  disablePowerUpMutation.isPending
                    ? (disablePowerUpMutation.variables?.boardPowerUpId ?? null)
                    : null
                }
                personalSettingsBusyId={
                  removePersonalPowerUpSettingsMutation.isPending
                    ? (removePersonalPowerUpSettingsMutation.variables?.boardPowerUpId ??
                      null)
                    : null
                }
                rowErrors={powerUpRowErrors}
                onToggleHeaderVisibility={handleTogglePowerUpHeader}
                onSaveSettings={handleSavePowerUpSettings}
                onAuthorize={handleAuthorizePowerUp}
                onRevokeAuthorization={handleRevokePowerUpAuthorization}
                onDisable={handleDisablePowerUp}
                onRemovePersonalSettings={handleRemovePersonalPowerUpSettings}
                onRetry={() => void boardPowerUps.refetch()}
              />
              <PowerUpDirectory
                entries={powerUps.data?.powerUps ?? []}
                query={powerUpQuery}
                selectedCategory={powerUpCategory}
                selectedPricingModel={powerUpPricingModel}
                madeByUsOnly={powerUpMadeByUs}
                loading={powerUps.isPending}
                error={powerUps.error?.message}
                compact
                boardContext={{ boardId: board.id, boardTitle: board.title }}
                onQueryChange={setPowerUpQuery}
                onSearch={setPowerUpSearch}
                onSelectCategory={setPowerUpCategory}
                onSelectPricingModel={setPowerUpPricingModel}
                onMadeByUsOnlyChange={setPowerUpMadeByUs}
                onOpenDetail={(powerUp) => setPowerUpDetailId(powerUp.id)}
                onRetry={() => void powerUps.refetch()}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {mobile ? (
                <MobileParityNotice
                  kind="power-ups"
                  title={t('boardMenu.mobile.powerUps.title')}
                  body={powerUpMobileReason}
                  href={mobileBoardHref}
                />
              ) : null}
              <PowerUpDetailView
                powerUp={powerUpDetail.data ?? null}
                loading={powerUpDetail.isPending}
                error={powerUpDetail.error?.message}
                enabledOnBoard={
                  selectedPowerUpInstall !== undefined ||
                  powerUps.data?.powerUps.find((entry) => entry.id === powerUpDetailId)
                    ?.enabledOnBoard
                }
                compact
                action={
                  powerUpDetail.data === undefined ||
                  !selectedPowerUpInstallKnown ||
                  selectedPowerUpInstall !== undefined ? null : (
                    <div className="flex flex-col gap-2">
                      {canManagePowerUps && !mobile ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          disabled={
                            enablePowerUpMutation.isPending &&
                            enablePowerUpMutation.variables?.powerUpId ===
                              powerUpDetail.data.id
                          }
                          onClick={() => handleEnablePowerUp(powerUpDetail.data)}
                        >
                          {t('boardMenu.powerUp.enableOnBoard', {
                            powerUp: powerUpDetail.data.name,
                          })}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          disabled
                          disabledReason={
                            mobile && canManagePowerUps
                              ? undefined
                              : (powerUpsDisabledReason ?? undefined)
                          }
                        >
                          {t('boardMenu.powerUp.enableOnBoard', {
                            powerUp: powerUpDetail.data.name,
                          })}
                        </Button>
                      )}
                      {powerUpRowErrors[powerUpDetail.data.id] === undefined ? null : (
                        <p role="alert" className="text-sm text-bad">
                          {powerUpRowErrors[powerUpDetail.data.id]}
                        </p>
                      )}
                    </div>
                  )
                }
                onBack={() => setPowerUpDetailId(null)}
                onRetry={() => void powerUpDetail.refetch()}
              />
            </div>
          ))}

        {section === 'external-access' && (
          <div className="flex flex-col gap-4">
            <BoardEmailToBoardSection
              address={emailToBoard.data}
              lists={lists}
              loading={canUseEmailToBoard && emailToBoard.isPending}
              loadError={emailToBoard.error?.message}
              canConfigure={canUseEmailToBoard}
              disabledReason={emailToBoardDisabledReason}
              regenerateBusy={regenerateEmailToBoardMutation.isPending}
              settingsBusy={updateEmailToBoardSettingsMutation.isPending}
              regenerateError={regenerateEmailToBoardMutation.error?.message}
              settingsError={updateEmailToBoardSettingsMutation.error?.message}
              onRegenerate={handleRegenerateEmailToBoard}
              onSaveSettings={handleSaveEmailToBoardSettings}
            />
            <hr className="border-border" />
            <BoardCalendarFeedSection
              feed={calendarFeed.data}
              loading={calendarFeed.isPending}
              loadError={calendarFeed.error?.message}
              canEnable={canEnableCalendarFeed}
              disabledReason={calendarFeedEnableDisabledReason}
              tokenBusy={generateCalendarFeedTokenMutation.isPending}
              enabledBusy={updateCalendarFeedMutation.isPending}
              tokenError={generateCalendarFeedTokenMutation.error?.message}
              enabledError={updateCalendarFeedMutation.error?.message}
              onGenerateToken={handleGenerateCalendarFeedToken}
              onSetEnabled={handleSetCalendarFeedEnabled}
            />
          </div>
        )}

        {section === 'snapshots' && (
          <BoardSnapshotSection
            boardTitle={board.title}
            snapshotList={snapshots.data}
            loading={canManageSnapshots && snapshots.isPending}
            loadError={snapshots.error?.message}
            canManage={canManageSnapshots}
            disabledReason={snapshotsDisabledReason}
            enabledBusy={setSnapshotsEnabledMutation.isPending}
            retentionBusy={setSnapshotRetentionMutation.isPending}
            captureAttachmentsBusy={setSnapshotCaptureAttachmentsMutation.isPending}
            restoreBusySnapshotId={
              restoreSnapshotMutation.isPending
                ? (restoreSnapshotMutation.variables?.snapshotId ?? null)
                : null
            }
            enabledError={setSnapshotsEnabledMutation.error?.message}
            retentionError={setSnapshotRetentionMutation.error?.message}
            captureAttachmentsError={setSnapshotCaptureAttachmentsMutation.error?.message}
            restoreError={restoreSnapshotMutation.error?.message}
            restoreResult={snapshotRestoreResult}
            onSetEnabled={handleSetSnapshotsEnabled}
            onSetRetentionDays={handleSetSnapshotRetention}
            onSetCaptureAttachments={handleSetSnapshotCaptureAttachments}
            onRestore={handleRestoreSnapshot}
            onClearRestoreResult={() => {
              setSnapshotRestoreResult(null);
              resetRestoreSnapshot();
            }}
          />
        )}

        {section === 'share' && (
          <SharePanel
            item={{
              kind: 'board',
              title: board.title,
              path: boardPath(board.shortLink, board.slug),
              visibility: shareVisibility(board),
            }}
            origin={typeof window === 'undefined' ? '' : window.location.origin}
            onClose={closeShareSection}
          />
        )}

        {section === 'archived' && (
          <ArchivedItemsPanel
            boardId={board.id}
            canManageArchive={canManageArchive}
            disabledReason={archiveDisabledReason}
            onPermanentListDelete={(listId) =>
              onPermanentListDelete?.(listUndoSourceKey(listId))
            }
          />
        )}
      </aside>

      {/*
        Outside the `<aside>` on purpose: a focus-trapped modal is not part of
        the complementary landmark, and keeping it out means an Escape aimed at
        the confirmation never reaches the panel's own handler through React's
        portal-following event propagation.
      */}
      <BoardDangerConfirm
        action={confirming}
        boardTitle={board.title}
        busy={busy}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(null)}
      />
      <BoardCopyDialog
        open={copyOpen}
        boardTitle={board.title}
        workspaces={copyWorkspaces.data ?? []}
        defaultWorkspaceId={board.workspaceId}
        loading={copyWorkspaces.isPending}
        busy={copyBoard.isPending}
        cardsCopied={copyResult?.cardsCopied}
        copiedBoardPath={copyResult?.path}
        onCancel={closeCopyDialog}
        onCopy={handleCopyBoard}
      />
      <Dialog
        open={leaveConfirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setLeaveConfirmOpen(false);
        }}
        title={t('boardMenu.leave.title')}
        description={t('boardMenu.leave.description')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setLeaveConfirmOpen(false)}>
              {t('boardMenu.cancel')}
            </Button>
            <Button
              variant="danger"
              busy={
                removeMutation.isPending &&
                removeMutation.variables?.userId === profile?.id
              }
              onClick={handleLeaveBoard}
            >
              {t('boardMenu.leaveBoard')}
            </Button>
          </>
        }
      >
        {leaveError === null ? null : (
          <p role="alert" className="text-sm text-bad">
            {leaveError}
          </p>
        )}
      </Dialog>
      <Dialog
        open={integrationDisableTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setIntegrationDisableTarget(null);
        }}
        title={
          integrationDisableTarget === null
            ? t('boardMenu.integration.disableTitle')
            : t('boardMenu.integration.disableNamedTitle', {
                integration: integrationDisableTarget.name,
              })
        }
        description={t('boardMenu.integration.disableDescription')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIntegrationDisableTarget(null)}>
              {t('boardMenu.cancel')}
            </Button>
            <Button
              variant="danger"
              busy={
                disablePowerUpMutation.isPending &&
                disablePowerUpMutation.variables?.boardPowerUpId ===
                  integrationDisableTarget?.boardPowerUpId
              }
              onClick={confirmDisableFirstPartyIntegration}
            >
              {integrationDisableTarget === null
                ? t('boardMenu.integration.disable')
                : t('boardMenu.integration.disableNamed', {
                    integration: integrationDisableTarget.name,
                  })}
            </Button>
          </>
        }
      >
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-1 size-4 accent-accent"
            checked={integrationDisableKeepData}
            onChange={(event) =>
              setIntegrationDisableKeepData(event.currentTarget.checked)
            }
          />
          <span>{t('boardMenu.integration.keepDataDescription')}</span>
        </label>
      </Dialog>
    </>
  );
}
